import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HardHat, Plus, Calendar, DollarSign, Users, Clock, CheckCircle2, Upload, FileText, RefreshCw, Download, Eye, MessageCircle } from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, GlassTable, GlassChart, GlassModal, GlassSelect, GlassTextarea, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';
import { downloadDocument, previewDocument } from '../../utils/documentViewer';

async function authOnlyHeaders() {
  const token = localStorage.getItem('nexus_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const tabs = [
  { id: 'implantacao', label: 'Implantação' },
  { id: 'obras', label: 'Obras' },
  { id: 'comercial', label: 'Comercial' },
  { id: 'crm', label: 'CRM Construtora' },
  { id: 'pedidos', label: 'Pedidos' },
  { id: 'fornecedores', label: 'Fornecedores' },
  { id: 'clientes', label: 'Clientes' },
  { id: 'parceiros', label: 'Parceiros' },
  { id: 'unidades', label: 'Unidades' },
  { id: 'recebiveis', label: 'Recebíveis' },
  { id: 'terrenos', label: 'Terrenos' },
  { id: 'viabilidade', label: 'Viabilidade' },
  { id: 'cronograma', label: 'Cronograma' },
  { id: 'custos', label: 'Custos' },
];

const openDocumentosOCR = (tipo = 'nota_obra') => {
  const params = new URLSearchParams({ acao: 'importar', modulo: 'construtora', tipo });
  window.location.assign(`/erp/documentos?${params.toString()}`);
};

const INITIAL_ORDER_FORM = {
  obra_id: '',
  etapa_id: '',
  fornecedor_nome: '',
  numero: '',
  data_emissao: '',
  data_vencimento: new Date().toISOString().slice(0, 10),
  valor_total: '',
  observacoes: '',
  itens: [],
};

const INITIAL_CLIENT_FORM = {
  nome: '',
  tipo_pessoa: 'fisica',
  cpf_cnpj: '',
  email: '',
  telefone: '',
  endereco: '',
  cidade: '',
  estado: '',
  vincular_lancamentos_sem_fornecedor: false,
  observacoes: '',
  mailing_lancamentos: true,
  status_relacionamento: 'normal',
  motivo_bloqueio_mailing: '',
};

const INITIAL_LEAD_FORM = {
  nome: '', cpf_cnpj: '', email: '', telefone: '', origem_canal: 'manual', origem_detalhe: '', campanha: '',
  obra_id: '', unidade_id: '', valor_minimo: '', valor_maximo: '', etapa: 'novo', temperatura: 'morno',
  responsavel: '', proxima_acao: '', proxima_acao_em: '', observacoes: '',
};

const CRM_STAGES = [
  ['novo', 'Novo'], ['qualificacao', 'Qualificação'], ['contato', 'Contato'], ['visita', 'Visita'],
  ['proposta', 'Proposta'], ['reserva', 'Reserva'], ['contrato', 'Contrato'], ['ganho', 'Ganho'],
  ['nutricao', 'Nutrição'], ['perdido', 'Perdido'],
];

const CRM_ORIGINS = [
  ['manual', 'Cadastro manual'], ['campanha', 'Campanha paga'], ['site', 'Site'], ['instagram', 'Instagram'],
  ['parceiro', 'Imobiliária/corretor parceiro'], ['feira_evento', 'Feira ou evento'], ['indicacao', 'Indicação'],
  ['cliente_base', 'Cliente da base'], ['whatsapp', 'WhatsApp'], ['outro', 'Outro'],
];

const INITIAL_SUPPLIER_FORM = {
  id: '',
  nome: '',
  tipo: 'material',
  cnpj: '',
  contato: '',
  telefone: '',
  email: '',
  endereco: '',
  cidade: '',
  estado: '',
};

const INITIAL_OBRA_FORM = {
  codigo: '',
  nome: '',
  descricao: '',
  endereco: '',
  cidade: '',
  estado: 'SC',
  data_inicio_prevista: '',
  data_fim_prevista: '',
  orcamento_previsto: '',
  status: 'planejamento',
  progresso: 0,
};

const INITIAL_ETAPA_FORM = {
  nome: '',
  descricao: '',
  ordem: '',
  data_inicio_prevista: '',
  data_fim_prevista: '',
  custo_previsto: '',
  status: 'pendente',
  progresso: 0,
};

const INITIAL_PARTNER_FORM = {
  nome: '',
  razao_social: '',
  cnpj: '',
  creci: '',
  responsavel: '',
  telefone: '',
  email: '',
  data_inicio_autorizacao: '',
  data_fim_autorizacao: '',
  percentual_comissao: '',
  status: 'ativa',
  observacoes: '',
};

const INITIAL_PARTNER_SALE_FORM = {
  imobiliaria_id: '',
  unidade_id: '',
  cliente_id: '',
  contrato_id: '',
  data_venda: new Date().toISOString().slice(0, 10),
  valor_venda: '',
  valor_comissao: '',
  corretor_nome: '',
  status: 'registrada',
  observacoes: '',
};

const INITIAL_UNIT_FORM = {
  obra_id: '',
  codigo: '',
  tipo: 'apartamento',
  bloco: '',
  pavimento: '',
  area_privativa: '',
  quartos: '',
  suites: '',
  vagas: '',
  status: 'disponivel',
  valor_tabela: '',
  data_entrega_prevista: '',
  observacoes: '',
};

const INITIAL_SALES_TABLE_FORM = {
  obra_id: '',
};

const INITIAL_LAND_FORM = {
  codigo: '',
  nome: '',
  endereco: '',
  cidade: '',
  estado: '',
  area_total: '',
  zoneamento: '',
  matricula: '',
  proprietario_nome: '',
  proprietario_documento: '',
  proprietario_telefone: '',
  proprietario_email: '',
  valor_pedido: '',
  valor_avaliado: '',
  proposta_permuta: '',
  percentual_permuta: '',
  unidades_permuta: '',
  status: 'avaliacao',
  obra_id: '',
  observacoes: '',
};

const INITIAL_MAINTENANCE_FORM = {
  unidade_id: '',
  data_abertura: new Date().toISOString().slice(0, 10),
  problema: '',
  solucao: '',
  responsavel: '',
  custo: '',
  status: 'aberta',
  observacoes: '',
};

const INITIAL_CONTRACT_FORM = {
  cliente_id: '',
  unidade_id: '',
  numero: '',
  data_assinatura: '',
  data_entrega_prevista: '',
  valor_total: '',
  valor_entrada: '',
  saldo_financiado: '',
  indice_correcao: '',
  forma_pagamento: '',
  parcelas: [],
};

const INITIAL_INDEX_FORM = {
  codigo: 'CUB_SC',
  competencia: new Date().toISOString().slice(0, 7),
  percentual: '',
  valor_indice: '',
  fonte: '',
  observacoes: '',
};

const INITIAL_COST_FORECAST_FORM = {
  terreno_id: '',
  area_total_m2: '',
  unidades: '',
  cidade: '',
  padrao: 'medio',
  tipo_estrutura: 'convencional',
  margem_seguranca_percentual: '12',
  obra_referencia_id: '',
  indice_atualizacao: '',
  competencia_alvo: new Date().toISOString().slice(0, 7),
  vgv_total: '',
  preco_m2_venda: '',
  ticket_medio_unidade: '',
  permuta_percentual: '',
  permuta_valor: '',
  comissao_percentual: '5',
  impostos_percentual: '6',
  marketing_percentual: '2',
  outros_custos: '',
};

const INDEX_TEMPLATE_ROWS = [
  ['codigo', 'competencia', 'percentual', 'valor_indice', 'fonte', 'observacoes'],
  ['INCC', '2026-01', '0,45', '', 'FGV', 'indice mensal durante obra'],
  ['CUB_SC', '2026-01', '0,32', '', 'Sinduscon SC', 'custo unitario basico SC'],
  ['IGPM', '2026-01', '0,27', '', 'FGV', 'correcao pos-chaves'],
  ['IPCA', '2026-01', '0,16', '', 'IBGE', 'referencia complementar'],
];

const INITIAL_CLIENT_DOC_FORM = {
  tipo: 'documento',
  unidade_id: '',
  contrato_id: '',
  observacao: '',
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const normalizeLookup = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toUpperCase();

export const Construtora = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('implantacao');
  const [obras, setObras] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [supplierHistory, setSupplierHistory] = useState([]);
  const [supplierSummary, setSupplierSummary] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierHistorySearch, setSupplierHistorySearch] = useState('');
  const [clientes, setClientes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadAgenda, setLeadAgenda] = useState([]);
  const [parceiros, setParceiros] = useState([]);
  const [vendasParceiros, setVendasParceiros] = useState([]);
  const [partnerSummary, setPartnerSummary] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [contratosVenda, setContratosVenda] = useState([]);
  const [parcelasReceber, setParcelasReceber] = useState([]);
  const [receivableFilters, setReceivableFilters] = useState({ mes: '', obra: '', cliente: '', status: '', grupo: 'mes' });
  const [terrenos, setTerrenos] = useState([]);
  const [landSummary, setLandSummary] = useState(null);
  const [landIntel, setLandIntel] = useState(null);
  const [commercialSummary, setCommercialSummary] = useState(null);
  const [implementationChecklist, setImplementationChecklist] = useState(null);
  const [indicesCorrecao, setIndicesCorrecao] = useState([]);
  const [costForecastSummary, setCostForecastSummary] = useState(null);
  const [costForecast, setCostForecast] = useState(null);
  const [viabilityStudies, setViabilityStudies] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isConsumeModalOpen, setIsConsumeModalOpen] = useState(false);
  const [modal, setModal] = useState('');
  const [orderForm, setOrderForm] = useState(INITIAL_ORDER_FORM);
  const [supplierForm, setSupplierForm] = useState(INITIAL_SUPPLIER_FORM);
  const [consumeForm, setConsumeForm] = useState({ obra_id: '', etapa_id: '', material_id: '', quantidade: '', valor_unitario: '', observacoes: '' });
  const [clientForm, setClientForm] = useState(INITIAL_CLIENT_FORM);
  const [leadForm, setLeadForm] = useState(INITIAL_LEAD_FORM);
  const [leadDetail, setLeadDetail] = useState(null);
  const [leadInteractionForm, setLeadInteractionForm] = useState({ tipo: 'nota', descricao: '', responsavel: '', data_agendada: '', status: 'realizada' });
  const [reconciliationReport, setReconciliationReport] = useState(null);
  const [correctionChangeForm, setCorrectionChangeForm] = useState({ indice_correcao: 'SELIC+1', juros_mensal_percentual: '1', vigencia_em: '', fase_correcao: 'pos_chaves' });
  const [correctionChangeResult, setCorrectionChangeResult] = useState(null);
  const [obraForm, setObraForm] = useState(INITIAL_OBRA_FORM);
  const [etapaForm, setEtapaForm] = useState(INITIAL_ETAPA_FORM);
  const [editingStageId, setEditingStageId] = useState('');
  const [partnerForm, setPartnerForm] = useState(INITIAL_PARTNER_FORM);
  const [partnerSaleForm, setPartnerSaleForm] = useState(INITIAL_PARTNER_SALE_FORM);
  const [unitForm, setUnitForm] = useState(INITIAL_UNIT_FORM);
  const [salesTableForm, setSalesTableForm] = useState(INITIAL_SALES_TABLE_FORM);
  const [landForm, setLandForm] = useState(INITIAL_LAND_FORM);
  const [maintenanceForm, setMaintenanceForm] = useState(INITIAL_MAINTENANCE_FORM);
  const [contractForm, setContractForm] = useState(INITIAL_CONTRACT_FORM);
  const [indexForm, setIndexForm] = useState(INITIAL_INDEX_FORM);
  const [costForecastForm, setCostForecastForm] = useState(INITIAL_COST_FORECAST_FORM);
  const [contractPdfFile, setContractPdfFile] = useState(null);
  const [contractPreview, setContractPreview] = useState(null);
  const [chargePreview, setChargePreview] = useState(null);
  const [salesTableFile, setSalesTableFile] = useState(null);
  const [indexImportFile, setIndexImportFile] = useState(null);
  const [salesTablePreview, setSalesTablePreview] = useState(null);
  const [unitDossier, setUnitDossier] = useState(null);
  const [clientDossier, setClientDossier] = useState(null);
  const [clientDocFile, setClientDocFile] = useState(null);
  const [clientDocForm, setClientDocForm] = useState(INITIAL_CLIENT_DOC_FORM);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [supplierHistoricalLinkMode, setSupplierHistoricalLinkMode] = useState(false);
  const [phaseReadingWorkId, setPhaseReadingWorkId] = useState('');
  const [phaseReadings, setPhaseReadings] = useState([]);
  const [phaseReadingLoading, setPhaseReadingLoading] = useState(false);
  const [phaseConfirmation, setPhaseConfirmation] = useState(null);
  const [scheduleWorkId, setScheduleWorkId] = useState('');
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [obrasRes, pedidosRes, fornecedoresRes, supplierHistoryRes, clientesRes, leadsRes, leadAgendaRes, unidadesRes, contratosRes, parcelasRes, terrenosRes, landSummaryRes, landIntelRes, commercialSummaryRes, implementationRes, indicesRes, parceirosRes, vendasParceirosRes, partnerSummaryRes, costForecastRes, viabilityStudiesRes] = await Promise.all([
        api.get('/construtora/obras', { params: { per_page: 200 } }),
        api.get('/construtora/pedidos-compra'),
        api.get('/construtora/fornecedores'),
        api.get('/construtora/fornecedores/historico-materiais'),
        api.get('/construtora/clientes'),
        api.get('/construtora/leads'),
        api.get('/construtora/leads/agenda'),
        api.get('/construtora/unidades'),
        api.get('/construtora/contratos-venda'),
        api.get('/construtora/parcelas-receber'),
        api.get('/construtora/terrenos'),
        api.get('/construtora/terrenos/patrimonio'),
        api.get('/construtora/terrenos/inteligencia-imobiliaria'),
        api.get('/construtora/comercial/resumo'),
        api.get('/construtora/implantacao/checklist'),
        api.get('/construtora/indices-correcao'),
        api.get('/construtora/imobiliarias-parceiras'),
        api.get('/construtora/imobiliarias-parceiras/vendas'),
        api.get('/construtora/imobiliarias-parceiras/resumo'),
        api.get('/construtora/previsao-custo-obra'),
        api.get('/construtora/estudos-viabilidade'),
      ]);
      setObras(obrasRes.data.items || []);
      setPedidos(pedidosRes.data.items || []);
      setFornecedores(fornecedoresRes.data.items || []);
      setSupplierHistory(supplierHistoryRes.data.items || []);
      setSupplierSummary(supplierHistoryRes.data.resumo_fornecedores || []);
      setClientes(clientesRes.data.items || []);
      setLeads(leadsRes.data.items || []);
      setLeadAgenda([...(leadAgendaRes.data.leads || []), ...(leadAgendaRes.data.interacoes || [])]);
      setUnidades(unidadesRes.data.items || []);
      setContratosVenda(contratosRes.data.items || []);
      setParcelasReceber(parcelasRes.data.items || []);
      setTerrenos(terrenosRes.data.items || []);
      setLandSummary(landSummaryRes.data || null);
      setLandIntel(landIntelRes.data || null);
      setCommercialSummary(commercialSummaryRes.data || null);
      setImplementationChecklist(implementationRes.data || null);
      setIndicesCorrecao(indicesRes.data.items || []);
      setParceiros(parceirosRes.data.items || []);
      setVendasParceiros(vendasParceirosRes.data.items || []);
      setPartnerSummary(partnerSummaryRes.data || null);
      setCostForecastSummary(costForecastRes.data || null);
      setViabilityStudies(viabilityStudiesRes.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar construtora.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const action = searchParams.get('acao');
    if (!action) return;
    const supplierName = (searchParams.get('nome') || '').trim();
    const supplierId = (searchParams.get('fornecedor_id') || '').trim();
    const scheduleObraId = (searchParams.get('obra_id') || '').trim();
    const tabActions = {
      comercial: () => setActiveTab('comercial'),
      crm: () => setActiveTab('crm'),
      clientes: () => setActiveTab('clientes'),
      unidades: () => setActiveTab('unidades'),
      recebiveis: () => setActiveTab('recebiveis'),
      terrenos: () => setActiveTab('terrenos'),
      viabilidade: () => setActiveTab('viabilidade'),
      cronograma: () => { setActiveTab('cronograma'); setScheduleWorkId(scheduleObraId); },
      obras: () => setActiveTab('obras'),
      fornecedores: () => setActiveTab('fornecedores'),
    };
    const modalActions = {
      novo_cliente: () => { setActiveTab('clientes'); setModal('cliente'); },
      novo_contrato: () => { setActiveTab('recebiveis'); setModal('contrato'); },
      novo_indice: () => { setActiveTab('recebiveis'); setModal('indice'); },
      nova_unidade: () => { setActiveTab('unidades'); setModal('unidade'); },
      tabela_vendas: () => { setActiveTab('unidades'); setModal('tabela-vendas'); },
      novo_terreno: () => { setActiveTab('terrenos'); setModal('terreno'); },
      nova_manutencao: () => { setActiveTab('unidades'); setModal('manutencao'); },
      novo_pedido: () => { setActiveTab('pedidos'); setIsOrderModalOpen(true); },
      parceiro: () => { setActiveTab('parceiros'); setModal('parceiro'); },
      novo_lead: () => { setActiveTab('crm'); setModal('lead'); },
      novo_fornecedor: () => {
        setActiveTab('fornecedores');
        setSupplierForm({ ...INITIAL_SUPPLIER_FORM, nome: supplierName, vincular_lancamentos_sem_fornecedor: true });
        setModal('fornecedor');
      },
      vincular_fornecedor: () => {
        const fornecedor = fornecedores.find((item) => item.id === supplierId);
        if (!fornecedor) return false;
        setActiveTab('fornecedores');
        setSupplierForm({ ...INITIAL_SUPPLIER_FORM, ...fornecedor });
        setSupplierHistoricalLinkMode(true);
        setModal('fornecedor');
        return true;
      },
    };
    const handler = modalActions[action] || tabActions[action];
    const handled = handler ? handler() : true;
    if (handled === false) return;
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, fornecedores]);

  const obraOptions = useMemo(() => obras.map((obra) => ({ value: obra.id, label: obra.nome || obra.codigo })), [obras]);
  const clienteOptions = useMemo(() => clientes.map((cliente) => ({ value: cliente.id, label: cliente.nome })), [clientes]);
  const parceiroOptions = useMemo(() => parceiros.map((parceiro) => ({ value: parceiro.id, label: parceiro.nome })), [parceiros]);
  const terrenoOptions = useMemo(() => terrenos.map((terreno) => ({ value: terreno.id, label: terreno.nome || terreno.codigo })), [terrenos]);
  const unidadeOptions = useMemo(() => unidades.map((unidade) => ({ value: unidade.id, label: `${unidade.codigo} · ${unidade.obra || 'sem obra'}` })), [unidades]);
  const filteredFornecedores = useMemo(() => {
    const term = supplierSearch.trim().toLowerCase();
    if (!term) return fornecedores;
    return fornecedores.filter((fornecedor) => [
      fornecedor.nome,
      fornecedor.cnpj,
      fornecedor.contato,
      fornecedor.email,
      fornecedor.telefone,
      ...(fornecedor.materiais || []),
    ].join(' ').toLowerCase().includes(term));
  }, [fornecedores, supplierSearch]);
  const totalPedidos = pedidos.reduce((acc, pedido) => acc + Number(pedido.valor_total || 0), 0);
  const pedidosPorObra = useMemo(() => pedidos.reduce((acc, pedido) => {
    const obraId = pedido.obra_id || 'sem_obra';
    acc[obraId] = (acc[obraId] || 0) + Number(pedido.valor_total || 0);
    return acc;
  }, {}), [pedidos]);
  const costChartData = useMemo(() => {
    const bySupplier = pedidos.reduce((acc, pedido) => {
      const label = pedido.fornecedor_nome || 'Sem fornecedor';
      acc[label] = (acc[label] || 0) + Number(pedido.valor_total || 0);
      return acc;
    }, {});
    const rows = Object.entries(bySupplier)
      .map(([name, value]) => ({ name, value: Number(value.toFixed(2)) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    return rows.length ? rows : [{ name: 'Sem custos lançados', value: 0 }];
  }, [pedidos]);
  const totalReceber = parcelasReceber
    .filter((parcela) => parcela.status !== 'pago')
    .reduce((acc, parcela) => acc + Number(parcela.valor_corrigido || parcela.valor_original || 0), 0);
  const receivableMonthOptions = useMemo(() => {
    const months = [...new Set(parcelasReceber.map((parcela) => String(parcela.vencimento || '').slice(0, 7)).filter(Boolean))].sort();
    return months.map((month) => ({ value: month, label: month.split('-').reverse().join('/') }));
  }, [parcelasReceber]);
  const filteredParcelasReceber = useMemo(() => parcelasReceber.filter((parcela) => {
    if (receivableFilters.mes && !String(parcela.vencimento || '').startsWith(receivableFilters.mes)) return false;
    if (receivableFilters.obra && parcela.obra !== receivableFilters.obra) return false;
    if (receivableFilters.cliente && parcela.cliente !== receivableFilters.cliente) return false;
    if (receivableFilters.status && parcela.status !== receivableFilters.status) return false;
    return true;
  }), [parcelasReceber, receivableFilters]);
  const receivableGroups = useMemo(() => {
    const keyFns = {
      mes: (parcela) => String(parcela.vencimento || '').slice(0, 7) || 'Sem vencimento',
      obra: (parcela) => parcela.obra || 'Sem empreendimento',
      cliente: (parcela) => parcela.cliente || 'Sem cliente',
      status: (parcela) => parcela.status || 'Sem status',
    };
    const keyFn = keyFns[receivableFilters.grupo] || keyFns.mes;
    const grouped = filteredParcelasReceber.reduce((acc, parcela) => {
      const key = keyFn(parcela);
      if (!acc[key]) acc[key] = [];
      acc[key].push(parcela);
      return acc;
    }, {});
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({
        key,
        items,
        total: items.reduce((acc, parcela) => acc + Number(parcela.valor_corrigido || parcela.valor_original || 0), 0),
      }));
  }, [filteredParcelasReceber, receivableFilters.grupo]);
  const commercialStatusLabels = {
    disponivel: 'Disponível',
    reservado: 'Reservada',
    proposta: 'Proposta',
    contrato_enviado: 'Contrato enviado',
    vendido: 'Vendida',
    entregue: 'Entregue',
    distratado: 'Distratada',
  };

  const handleCostForecast = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await api.post('/construtora/viabilidade-empreendimento', {
        ...costForecastForm,
        area_total_m2: Number(String(costForecastForm.area_total_m2 || 0).replace(',', '.')),
        unidades: Number(String(costForecastForm.unidades || 0).replace(',', '.')),
        margem_seguranca_percentual: Number(String(costForecastForm.margem_seguranca_percentual || 0).replace(',', '.')),
        vgv_total: Number(String(costForecastForm.vgv_total || 0).replace(',', '.')),
        preco_m2_venda: Number(String(costForecastForm.preco_m2_venda || 0).replace(',', '.')),
        ticket_medio_unidade: Number(String(costForecastForm.ticket_medio_unidade || 0).replace(',', '.')),
        permuta_percentual: Number(String(costForecastForm.permuta_percentual || 0).replace(',', '.')),
        permuta_valor: Number(String(costForecastForm.permuta_valor || 0).replace(',', '.')),
        comissao_percentual: Number(String(costForecastForm.comissao_percentual || 0).replace(',', '.')),
        impostos_percentual: Number(String(costForecastForm.impostos_percentual || 0).replace(',', '.')),
        marketing_percentual: Number(String(costForecastForm.marketing_percentual || 0).replace(',', '.')),
        outros_custos: Number(String(costForecastForm.outros_custos || 0).replace(',', '.')),
      });
      setCostForecast(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao calcular previsao de custo.');
    } finally {
      setSaving(false);
    }
  };

  const saveViabilityStudy = async (status = 'em_analise') => {
    if (!costForecast) {
      setError('Calcule a viabilidade antes de salvar o estudo.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const selectedTerrain = terrenos.find((item) => item.id === costForecastForm.terreno_id);
      await api.post('/construtora/estudos-viabilidade', {
        ...costForecastForm,
        nome: selectedTerrain ? `Viabilidade - ${selectedTerrain.nome}` : `Viabilidade ${new Date().toLocaleDateString('pt-BR')}`,
        cenario: 'provavel',
        status,
        resultado: costForecast,
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar estudo de viabilidade.');
    } finally {
      setSaving(false);
    }
  };

  const updateViabilityStudy = async (study, status) => {
    setSaving(true);
    setError('');
    try {
      await api.patch(`/construtora/estudos-viabilidade/${study.id}`, { status });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao atualizar estudo de viabilidade.');
    } finally {
      setSaving(false);
    }
  };

  const convertViabilityStudyToWork = async (study) => {
    setSaving(true);
    setError('');
    try {
      await api.post(`/construtora/estudos-viabilidade/${study.id}/converter-obra`, {
        nome: study.terreno ? `Obra ${study.terreno}` : study.nome,
        status: 'planejamento',
        etapas_padrao: true,
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao converter estudo em obra.');
    } finally {
      setSaving(false);
    }
  };

  const loadEtapas = async (obraId) => {
    if (!obraId) {
      setEtapas([]);
      return;
    }
    try {
      const response = await api.get(`/construtora/obras/${obraId}/etapas`);
      setEtapas(response.data.etapas || []);
    } catch (err) {
      setEtapas([]);
    }
  };

  const handleOrderChange = (field, value) => {
    setOrderForm((current) => ({ ...current, [field]: value }));
  };

  const handleObraChange = async (value) => {
    setOrderForm((current) => ({ ...current, obra_id: value, etapa_id: '' }));
    await loadEtapas(value);
  };

  const handleImportPdf = async () => {
    if (!orderForm.obra_id || !pdfFile) {
      setError('Selecione uma obra e um PDF da nota.');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('obra_id', orderForm.obra_id);
      if (orderForm.etapa_id) formData.append('etapa_id', orderForm.etapa_id);
      formData.append('arquivo', pdfFile);
      const response = await api.post('/construtora/pedidos-compra/importar-pdf', formData);
      const extracted = response.data.extracted || {};
      setPdfPreview(response.data);
      setOrderForm((current) => ({
        ...current,
        fornecedor_nome: extracted.pessoa_nome || current.fornecedor_nome,
        numero: extracted.numero_documento || current.numero,
        data_emissao: extracted.data_emissao || current.data_emissao,
        data_vencimento: extracted.data_vencimento || current.data_vencimento,
        valor_total: extracted.valor ? String(extracted.valor) : current.valor_total,
        observacoes: extracted.descricao || current.observacoes,
        itens: extracted.itens || [],
      }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao importar nota da obra.');
    } finally {
      setImporting(false);
    }
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/construtora/pedidos-compra', {
        ...orderForm,
        arquivo_id: pdfPreview?.arquivo?.id,
        valor_total: Number(String(orderForm.valor_total).replace(',', '.')),
        criar_conta_pagar: true,
        raw_extracted: pdfPreview?.extracted,
      });
      setIsOrderModalOpen(false);
      setOrderForm(INITIAL_ORDER_FORM);
      setPdfFile(null);
      setPdfPreview(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar pedido de compra.');
    } finally {
      setSaving(false);
    }
  };

  const loadMateriais = async () => {
    try {
      const response = await api.get('/construtora/materiais', { params: { per_page: 300 } });
      setMateriais(response.data.items || []);
    } catch (err) {
      setMateriais([]);
    }
  };

  const handleReceiveOrder = async (pedidoId) => {
    setSaving(true);
    setError('');
    try {
      await api.post(`/construtora/pedidos-compra/${pedidoId}/receber`, {});
      await loadData();
      await loadMateriais();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao receber pedido.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveOrder = async (pedidoId) => {
    if (!window.confirm('Aprovar este pedido? Isso não registra recebimento físico nem pagamento.')) return;
    setSaving(true);
    setError('');
    try {
      await api.post(`/construtora/pedidos-compra/${pedidoId}/aprovar`, {});
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao aprovar pedido.');
    } finally {
      setSaving(false);
    }
  };

  const openConsumeModal = async () => {
    await loadMateriais();
    setIsConsumeModalOpen(true);
  };

  const handleConsumeChange = (field, value) => {
    setConsumeForm((current) => ({ ...current, [field]: value }));
  };

  const handleConsumeObraChange = async (value) => {
    setConsumeForm((current) => ({ ...current, obra_id: value, etapa_id: '' }));
    await loadEtapas(value);
  };

  const handleConsumeMaterialChange = (value) => {
    const material = materiais.find((item) => item.id === value);
    setConsumeForm((current) => ({
      ...current,
      material_id: value,
      valor_unitario: material?.custo_unitario ? String(material.custo_unitario) : current.valor_unitario,
    }));
  };

  const handleSaveConsumption = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post(`/construtora/obras/${consumeForm.obra_id}/materiais/consumir`, {
        ...consumeForm,
        quantidade: Number(String(consumeForm.quantidade).replace(',', '.')),
        valor_unitario: Number(String(consumeForm.valor_unitario).replace(',', '.')),
      });
      setIsConsumeModalOpen(false);
      setConsumeForm({ obra_id: '', etapa_id: '', material_id: '', quantidade: '', valor_unitario: '', observacoes: '' });
      await loadData();
      await loadMateriais();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao consumir material.');
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setModal('');
    setError('');
    setChargePreview(null);
    setClientDocFile(null);
    setClientDocForm(INITIAL_CLIENT_DOC_FORM);
    setSupplierForm(INITIAL_SUPPLIER_FORM);
    setSupplierHistoricalLinkMode(false);
    setLeadDetail(null);
    setLeadInteractionForm({ tipo: 'nota', descricao: '', responsavel: '', data_agendada: '', status: 'realizada' });
    setReconciliationReport(null);
    setCorrectionChangeResult(null);
  };

  const openObraModal = (obra = null) => {
    setObraForm(obra ? {
      ...INITIAL_OBRA_FORM,
      ...obra,
      orcamento_previsto: obra.orcamento_previsto ? String(obra.orcamento_previsto) : '',
      progresso: obra.progresso ?? 0,
    } : INITIAL_OBRA_FORM);
    setModal('obra');
  };

  const saveObra = async () => {
    if (!obraForm.codigo || !obraForm.nome) {
      setError('Código e nome da obra são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...obraForm,
        progresso: Number(obraForm.progresso || 0),
        orcamento_previsto: obraForm.orcamento_previsto ? Number(String(obraForm.orcamento_previsto).replace(',', '.')) : null,
      };
      if (obraForm.id) {
        await api.put(`/construtora/obras/${obraForm.id}`, payload);
      } else {
        await api.post('/construtora/obras', payload);
      }
      setObraForm(INITIAL_OBRA_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar obra.');
    } finally {
      setSaving(false);
    }
  };

  const deleteObra = async (obra) => {
    if (!obra?.id) return;
    if (!window.confirm(`Excluir a obra ${obra.nome}? Obras com vínculos não serão excluídas.`)) return;
    setSaving(true);
    try {
      await api.delete(`/construtora/obras/${obra.id}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao excluir obra.');
    } finally {
      setSaving(false);
    }
  };

  const openClientModal = (cliente = null) => {
    setClientForm(cliente ? {
      ...INITIAL_CLIENT_FORM,
      ...cliente,
      mailing_lancamentos: cliente.mailing_lancamentos !== false,
    } : INITIAL_CLIENT_FORM);
    setModal('cliente');
  };

  const saveClient = async () => {
    if (!clientForm.nome) {
      setError('Nome do cliente é obrigatório.');
      return;
    }
    setSaving(true);
    try {
      if (clientForm.id) {
        await api.patch(`/construtora/clientes/${clientForm.id}`, clientForm);
      } else {
        await api.post('/construtora/clientes', clientForm);
      }
      setClientForm(INITIAL_CLIENT_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar cliente.');
    } finally {
      setSaving(false);
    }
  };

  const openLeadModal = async (lead = null) => {
    setLeadForm(lead ? {
      ...INITIAL_LEAD_FORM,
      ...lead,
      valor_minimo: lead.valor_minimo ? String(lead.valor_minimo) : '',
      valor_maximo: lead.valor_maximo ? String(lead.valor_maximo) : '',
      proxima_acao_em: lead.proxima_acao_em ? lead.proxima_acao_em.slice(0, 16) : '',
    } : INITIAL_LEAD_FORM);
    setLeadDetail(null);
    setModal('lead');
    if (lead?.id) {
      try {
        const response = await api.get(`/construtora/leads/${lead.id}`);
        setLeadDetail(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Não foi possível carregar o histórico desta oportunidade.');
      }
    }
  };

  const saveLead = async () => {
    if (!leadForm.nome) {
      setError('Nome do interessado é obrigatório.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...leadForm,
        valor_minimo: leadForm.valor_minimo ? Number(String(leadForm.valor_minimo).replace(',', '.')) : null,
        valor_maximo: leadForm.valor_maximo ? Number(String(leadForm.valor_maximo).replace(',', '.')) : null,
        proxima_acao_em: leadForm.proxima_acao_em || null,
      };
      if (leadForm.id) await api.patch(`/construtora/leads/${leadForm.id}`, payload);
      else await api.post('/construtora/leads', payload);
      setLeadForm(INITIAL_LEAD_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar oportunidade no CRM.');
    } finally {
      setSaving(false);
    }
  };

  const moveLeadStage = async (lead, etapa) => {
    if (lead.etapa === etapa) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`/construtora/leads/${lead.id}/etapa`, { etapa });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao mover oportunidade.');
    } finally {
      setSaving(false);
    }
  };

  const convertLeadToClient = async (lead) => {
    if (!window.confirm(`Converter ${lead.nome} em cliente da Construtora? Isso cria ou vincula somente o cadastro; não reserva unidade nem cria contrato.`)) return;
    setSaving(true);
    setError('');
    try {
      await api.post(`/construtora/leads/${lead.id}/converter-cliente`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao converter oportunidade em cliente.');
    } finally {
      setSaving(false);
    }
  };

  const saveLeadInteraction = async () => {
    if (!leadForm.id || !leadInteractionForm.descricao) {
      setError('Descreva a ligação, mensagem, visita ou tarefa.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post(`/construtora/leads/${leadForm.id}/interacoes`, {
        ...leadInteractionForm,
        data_agendada: leadInteractionForm.data_agendada || null,
      });
      const response = await api.get(`/construtora/leads/${leadForm.id}`);
      setLeadDetail(response.data);
      setLeadInteractionForm({ tipo: 'nota', descricao: '', responsavel: '', data_agendada: '', status: 'realizada' });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao registrar interação.');
    } finally {
      setSaving(false);
    }
  };

  const checkContractFinance = async (contrato) => {
    setSaving(true);
    setError('');
    try {
      const response = await api.get(`/construtora/contratos-venda/${contrato.id}/reconciliacao-financeira`);
      setReconciliationReport({ ...response.data, contrato });
      setCorrectionChangeForm({
        indice_correcao: 'SELIC+1',
        juros_mensal_percentual: '1',
        vigencia_em: contrato.data_entrega_prevista || '',
        fase_correcao: 'pos_chaves',
      });
      setCorrectionChangeResult(null);
      setModal('reconciliacao-financeira');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao verificar os vínculos financeiros do contrato.');
    } finally {
      setSaving(false);
    }
  };

  const applyContractFinanceReconciliation = async () => {
    if (!reconciliationReport?.contrato?.id) return;
    setSaving(true);
    setError('');
    try {
      const response = await api.post(`/construtora/contratos-venda/${reconciliationReport.contrato.id}/reconciliacao-financeira`);
      setReconciliationReport({ ...response.data, contrato: reconciliationReport.contrato, applied: true });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao aplicar a reconciliação financeira.');
    } finally {
      setSaving(false);
    }
  };

  const applyContractCorrectionRule = async () => {
    if (!reconciliationReport?.contrato?.id || !correctionChangeForm.vigencia_em) {
      setError('Informe a data de vigência da nova regra de correção.');
      return;
    }
    if (!window.confirm(`Alterar a regra para ${correctionChangeForm.indice_correcao} a partir de ${formatDate(correctionChangeForm.vigencia_em)}? Parcelas já pagas não serão modificadas.`)) return;
    setSaving(true);
    setError('');
    try {
      const response = await api.post(`/construtora/contratos-venda/${reconciliationReport.contrato.id}/regra-correcao`, {
        ...correctionChangeForm,
        juros_mensal_percentual: Number(String(correctionChangeForm.juros_mensal_percentual || '0').replace(',', '.')),
      });
      setCorrectionChangeResult(response.data);
      const report = await api.get(`/construtora/contratos-venda/${reconciliationReport.contrato.id}/reconciliacao-financeira`);
      setReconciliationReport({ ...report.data, contrato: response.data.contrato });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao atualizar a regra de correção.');
    } finally {
      setSaving(false);
    }
  };

  const loadSupplierHistory = async (search = supplierHistorySearch) => {
    setLoading(true);
    try {
      const response = await api.get('/construtora/fornecedores/historico-materiais', {
        params: { search: search || undefined },
      });
      setSupplierHistory(response.data.items || []);
      setSupplierSummary(response.data.resumo_fornecedores || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar histórico de fornecedores.');
    } finally {
      setLoading(false);
    }
  };

  const openSupplierModal = (fornecedor = null) => {
    setSupplierHistoricalLinkMode(false);
    setSupplierForm(fornecedor ? {
      ...INITIAL_SUPPLIER_FORM,
      ...fornecedor,
    } : INITIAL_SUPPLIER_FORM);
    setModal('fornecedor');
  };

  const saveSupplier = async () => {
    if (!supplierForm.nome) {
      setError('Nome do fornecedor é obrigatório.');
      return;
    }
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = { ...supplierForm };
      let response;
      if (payload.id) {
        response = await api.patch(`/construtora/fornecedores/${payload.id}`, payload);
      } else {
        response = await api.post('/construtora/fornecedores', payload);
      }
      const linkedCount = Number(response.data?.lancamentos_vinculados || 0);
      setSupplierForm(INITIAL_SUPPLIER_FORM);
      closeModal();
      await loadData();
      setSuccess(linkedCount > 0
        ? `Fornecedor salvo. ${linkedCount} lançamento(s) financeiro(s) histórico(s) foram vinculados a este cadastro.`
        : 'Fornecedor salvo com sucesso.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar fornecedor.');
    } finally {
      setSaving(false);
    }
  };

  const linkExistingSupplierHistory = async () => {
    if (!supplierForm.id) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const response = await api.post(`/construtora/fornecedores/${supplierForm.id}/vincular-lancamentos-historicos`);
      const linkedCount = Number(response.data?.lancamentos_vinculados || 0);
      closeModal();
      await loadData();
      setSuccess(linkedCount > 0
        ? `Vínculo confirmado. ${linkedCount} lançamento(s) financeiro(s) histórico(s) foram associados ao fornecedor.`
        : 'Nenhum lançamento elegível permaneceu sem vínculo para este fornecedor.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao vincular lançamentos históricos do fornecedor.');
    } finally {
      setSaving(false);
    }
  };

  const loadPhaseReadings = async (obraId = phaseReadingWorkId) => {
    if (!obraId) {
      setError('Selecione uma obra para analisar as notas e pedidos.');
      return;
    }
    setError('');
    setPhaseReadingLoading(true);
    try {
      const response = await api.get(`/construtora/obras/${obraId}/leitura-fases`);
      setPhaseReadings(response.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao analisar fases da obra.');
    } finally {
      setPhaseReadingLoading(false);
    }
  };

  const openPhaseConfirmation = async (reading) => {
    await loadEtapas(reading.obra_id);
    setPhaseConfirmation({
      ...reading,
      fase: reading.fase_sugerida || 'outros',
      metodo: reading.metodo_sugerido || 'indefinido',
      local_obra: reading.local_sugerido || '',
      etapa_id: '',
      observacao: '',
    });
    setModal('leitura-fase');
  };

  const confirmPhaseReading = async () => {
    if (!phaseConfirmation?.obra_id) return;
    setSaving(true);
    setError('');
    try {
      const response = await api.post(`/construtora/obras/${phaseConfirmation.obra_id}/leitura-fases/confirmar`, {
        pedido_ids: phaseConfirmation.pedido_ids,
        fase: phaseConfirmation.fase,
        metodo: phaseConfirmation.metodo,
        local_obra: phaseConfirmation.local_obra,
        etapa_id: phaseConfirmation.etapa_id,
        observacao: phaseConfirmation.observacao,
      });
      closeModal();
      setSuccess(`Leitura confirmada para ${response.data?.pedidos_confirmados || 0} pedido(s). A classificação agora alimenta o histórico de custos.`);
      await loadPhaseReadings(phaseConfirmation.obra_id);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao confirmar a leitura de fases.');
    } finally {
      setSaving(false);
    }
  };

  const loadSchedule = async (obraId = scheduleWorkId) => {
    if (!obraId) return setError('Selecione uma obra para carregar o cronograma real.');
    setScheduleLoading(true);
    setError('');
    try {
      const response = await api.get(`/construtora/obras/${obraId}/cronograma-fisico-financeiro`);
      setScheduleData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar cronograma físico-financeiro.');
    } finally {
      setScheduleLoading(false);
    }
  };

  const createScheduleStage = async () => {
    if (!scheduleWorkId) return setError('Selecione a obra antes de cadastrar uma etapa.');
    if (!etapaForm.nome.trim()) return setError('Informe o nome da etapa.');
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...etapaForm,
        ordem: etapaForm.ordem === '' ? 0 : Number(etapaForm.ordem),
        progresso: Number(etapaForm.progresso || 0),
        custo_previsto: etapaForm.custo_previsto === '' ? null : Number(etapaForm.custo_previsto),
      };
      if (editingStageId) await api.put(`/construtora/etapas/${editingStageId}`, payload);
      else await api.post(`/construtora/obras/${scheduleWorkId}/etapas`, payload);
      setModal('');
      setEtapaForm(INITIAL_ETAPA_FORM);
      setEditingStageId('');
      await loadEtapas(scheduleWorkId);
      await loadSchedule(scheduleWorkId);
      setSuccess(editingStageId ? 'Etapa atualizada.' : 'Etapa cadastrada. Agora confirme as notas que pertencem a ela na leitura mensal.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao cadastrar etapa.');
    } finally {
      setSaving(false);
    }
  };

  const createClientFromContractPreview = async () => {
    const extracted = contractPreview?.extracted || {};
    const nome = extracted.cliente_nome || clientForm.nome;
    if (!nome) {
      setError('A leitura não identificou o nome do cliente. Cadastre o cliente pela aba Clientes ou preencha manualmente depois.');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/construtora/clientes', {
        ...INITIAL_CLIENT_FORM,
        nome,
        cpf_cnpj: extracted.cliente_documento || '',
        observacoes: 'Criado a partir da leitura de contrato de venda.',
      });
      const cliente = response.data;
      setContractForm((current) => ({ ...current, cliente_id: cliente.id }));
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar cliente a partir do contrato.');
    } finally {
      setSaving(false);
    }
  };

  const createWorkUnitFromContractPreview = async () => {
    const extracted = contractPreview?.extracted || {};
    const empreendimentoNome = extracted.empreendimento_nome || '';
    const unidadeCodigo = extracted.unidade_codigo || '';
    if (!empreendimentoNome || !unidadeCodigo) {
      setError('A leitura precisa identificar empreendimento e unidade para criar a obra/unidade automaticamente.');
      return;
    }

    setSaving(true);
    try {
      const empreendimentoKey = normalizeLookup(empreendimentoNome);
      let obra = obras.find((item) => {
        const nomeKey = normalizeLookup(item.nome);
        const codigoKey = normalizeLookup(item.codigo);
        return nomeKey === empreendimentoKey || codigoKey === empreendimentoKey || nomeKey.includes(empreendimentoKey) || empreendimentoKey.includes(nomeKey);
      });

      if (!obra) {
        const obraResponse = await api.post('/construtora/obras', {
          ...INITIAL_OBRA_FORM,
          codigo: empreendimentoKey.slice(0, 40) || `OBRA-${Date.now()}`,
          nome: empreendimentoNome,
          cidade: 'Navegantes',
          estado: 'SC',
          status: 'em_andamento',
          data_inicio_prevista: contractForm.data_assinatura || extracted.data_assinatura || '',
          data_fim_prevista: contractForm.data_entrega_prevista || extracted.data_entrega_prevista || '',
          descricao: 'Criada a partir da leitura de contrato de venda.',
        });
        obra = obraResponse.data;
      }

      let unidade = unidades.find((item) => (
        item.obra_id === obra.id
        && normalizeLookup(item.codigo) === normalizeLookup(unidadeCodigo)
      ));

      if (!unidade) {
        const unitResponse = await api.post('/construtora/unidades', {
          ...INITIAL_UNIT_FORM,
          obra_id: obra.id,
          codigo: unidadeCodigo,
          tipo: 'apartamento',
          status: 'disponivel',
          valor_tabela: extracted.valor_total || contractForm.valor_total || '',
          data_entrega_prevista: contractForm.data_entrega_prevista || extracted.data_entrega_prevista || '',
          observacoes: 'Criada a partir da leitura de contrato de venda.',
        });
        unidade = unitResponse.data;
      }

      setContractForm((current) => ({ ...current, unidade_id: unidade.id }));
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar obra/unidade a partir do contrato.');
    } finally {
      setSaving(false);
    }
  };

  const savePartner = async () => {
    if (!partnerForm.nome) {
      setError('Nome da imobiliária parceira é obrigatório.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/construtora/imobiliarias-parceiras', partnerForm);
      setPartnerForm(INITIAL_PARTNER_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar imobiliária parceira.');
    } finally {
      setSaving(false);
    }
  };

  const savePartnerSale = async () => {
    if (!partnerSaleForm.imobiliaria_id) {
      setError('Selecione a imobiliária parceira.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/construtora/imobiliarias-parceiras/vendas', partnerSaleForm);
      setPartnerSaleForm(INITIAL_PARTNER_SALE_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao registrar venda da imobiliária.');
    } finally {
      setSaving(false);
    }
  };

  const openClientDossier = async (clienteId) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/construtora/clientes/${clienteId}/dossie`);
      setClientDossier(response.data);
      setClientDocForm(INITIAL_CLIENT_DOC_FORM);
      setClientDocFile(null);
      setModal('cliente-dossie');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dossiê do cliente.');
    } finally {
      setLoading(false);
    }
  };

  const uploadClientDocument = async () => {
    if (!clientDossier?.cliente?.id || !clientDocFile) {
      setError('Selecione um arquivo para anexar ao cliente.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('arquivo', clientDocFile);
      formData.append('tipo', clientDocForm.tipo || 'documento');
      if (clientDocForm.unidade_id) formData.append('unidade_id', clientDocForm.unidade_id);
      if (clientDocForm.contrato_id) formData.append('contrato_id', clientDocForm.contrato_id);
      if (clientDocForm.observacao) formData.append('observacao', clientDocForm.observacao);
      await api.post(`/construtora/clientes/${clientDossier.cliente.id}/documentos`, formData);
      await openClientDossier(clientDossier.cliente.id);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao anexar documento.');
    } finally {
      setSaving(false);
    }
  };

  const clientDocumentUrl = (documento) => `/erp/api/v1/construtora/clientes/documentos/${documento.id}/download`;
  const clientDocumentName = (documento) => documento?.nome_armazenado || documento?.nome_original || 'documento-cliente';

  const previewClientDocument = async (documento) => {
    setSaving(true);
    setError('');
    try {
      const headers = await authOnlyHeaders();
      await previewDocument(clientDocumentUrl(documento), clientDocumentName(documento), headers);
    } catch (err) {
      setError(err.message || 'Erro ao abrir documento.');
    } finally {
      setSaving(false);
    }
  };

  const downloadClientDocument = async (documento) => {
    setSaving(true);
    setError('');
    try {
      const headers = await authOnlyHeaders();
      await downloadDocument(clientDocumentUrl(documento), clientDocumentName(documento), headers);
    } catch (err) {
      setError(err.message || 'Erro ao baixar documento.');
    } finally {
      setSaving(false);
    }
  };

  const saveUnit = async () => {
    if (!unitForm.obra_id || !unitForm.codigo) {
      setError('Obra e código da unidade são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/construtora/unidades', unitForm);
      setUnitForm(INITIAL_UNIT_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar unidade.');
    } finally {
      setSaving(false);
    }
  };

  const updateUnitStatus = async (unidade, status) => {
    setSaving(true);
    setError('');
    try {
      await api.patch(`/construtora/unidades/${unidade.id}`, { status });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao atualizar status da unidade.');
    } finally {
      setSaving(false);
    }
  };

  const openUnitDossier = async (unidadeId) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/construtora/unidades/${unidadeId}/dossie`);
      setUnitDossier(response.data);
      setModal('unidade-dossie');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dossiê da unidade.');
    } finally {
      setLoading(false);
    }
  };

  const importSalesTable = async (confirmar = false) => {
    if (!salesTableForm.obra_id || !salesTableFile) {
      setError('Selecione a obra e o arquivo da tabela de vendas.');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('obra_id', salesTableForm.obra_id);
      formData.append('confirmar', confirmar ? 'true' : 'false');
      formData.append('arquivo', salesTableFile);
      const response = await api.post('/construtora/unidades/importar-tabela-vendas', formData);
      setSalesTablePreview(response.data);
      if (confirmar) {
        setSalesTableFile(null);
        setSalesTableForm(INITIAL_SALES_TABLE_FORM);
        closeModal();
        await loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao importar tabela de vendas.');
    } finally {
      setImporting(false);
    }
  };

  const saveLand = async () => {
    if (!landForm.nome) {
      setError('Nome do terreno é obrigatório.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/construtora/terrenos', landForm);
      setLandForm(INITIAL_LAND_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar terreno.');
    } finally {
      setSaving(false);
    }
  };

  const updateLandStatus = async (terreno, status) => {
    setSaving(true);
    try {
      await api.patch(`/construtora/terrenos/${terreno.id}`, { status });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao atualizar terreno.');
    } finally {
      setSaving(false);
    }
  };

  const saveMaintenance = async () => {
    if (!maintenanceForm.unidade_id || !maintenanceForm.problema) {
      setError('Unidade e problema são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/construtora/unidades/${maintenanceForm.unidade_id}/manutencoes`, maintenanceForm);
      setMaintenanceForm(INITIAL_MAINTENANCE_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao registrar manutenção.');
    } finally {
      setSaving(false);
    }
  };

  const importContractPdf = async () => {
    if (!contractPdfFile) {
      setError('Selecione o PDF do contrato.');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('arquivo', contractPdfFile);
      formData.append('automatico', 'true');
      const response = await api.post('/construtora/contratos-venda/importar-pdf', formData);
      const extracted = response.data.extracted || {};
      setContractPreview(response.data);
      if (response.data.auto_result?.status === 'automatico') {
        setContractForm(INITIAL_CONTRACT_FORM);
        setContractPdfFile(null);
        setContractPreview(null);
        closeModal();
        await loadData();
        return;
      }
      setContractForm((current) => ({
        ...current,
        data_assinatura: extracted.data_assinatura || current.data_assinatura,
        data_entrega_prevista: extracted.data_entrega_prevista || current.data_entrega_prevista,
        valor_total: extracted.valor_total ? String(extracted.valor_total) : current.valor_total,
        valor_entrada: extracted.valor_entrada ? String(extracted.valor_entrada) : current.valor_entrada,
        indice_correcao: extracted.indice_correcao || current.indice_correcao,
        forma_pagamento: extracted.forma_pagamento || current.forma_pagamento,
        parcelasTexto: Array.isArray(extracted.parcelas_sugeridas) && extracted.parcelas_sugeridas.length
          ? extracted.parcelas_sugeridas.map((parcela) => [
            parcela.vencimento,
            parcela.valor,
            parcela.tipo || 'parcela',
            parcela.indice_correcao || extracted.indice_correcao || '',
            parcela.fase_correcao || 'obra',
            parcela.juros_mensal_percentual || '',
          ].join('; ')).join('\n')
          : current.parcelasTexto,
      }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao ler contrato.');
    } finally {
      setImporting(false);
    }
  };

  const saveContract = async () => {
    if (!contractForm.cliente_id || !contractForm.unidade_id) {
      setError('Cliente e unidade são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const parcelas = String(contractForm.parcelasTexto || '')
        .split('\n')
        .map((line, index) => {
          const [
            vencimento,
            valor,
            tipo = 'parcela',
            indice_correcao = contractForm.indice_correcao,
            fase_correcao = 'obra',
            juros_mensal_percentual = '',
          ] = line.split(';').map((part) => part?.trim());
          return vencimento && valor ? {
            numero: index + 1,
            vencimento,
            valor,
            tipo,
            indice_correcao,
            fase_correcao,
            juros_mensal_percentual,
          } : null;
        })
        .filter(Boolean);
      await api.post('/construtora/contratos-venda', {
        ...contractForm,
        arquivo_id: contractPreview?.arquivo?.id,
        raw_extracted: contractPreview?.extracted,
        parcelas,
      });
      setContractForm(INITIAL_CONTRACT_FORM);
      setContractPdfFile(null);
      setContractPreview(null);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar contrato.');
    } finally {
      setSaving(false);
    }
  };

  const saveIndex = async () => {
    if (!indexForm.codigo || !indexForm.competencia || !indexForm.percentual) {
      setError('Índice, competência e percentual são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/construtora/indices-correcao', indexForm);
      setIndexForm(INITIAL_INDEX_FORM);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar índice.');
    } finally {
      setSaving(false);
    }
  };

  const importIndices = async () => {
    if (!indexImportFile) {
      setError('Selecione um arquivo CSV ou TXT com os índices.');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('arquivo', indexImportFile);
      await api.post('/construtora/indices-correcao/importar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setIndexImportFile(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao importar índices.');
    } finally {
      setImporting(false);
    }
  };

  const downloadIndexTemplate = () => {
    const csv = INDEX_TEMPLATE_ROWS.map((row) => row.join(';')).join('\n');
    const blob = new Blob([`\uFEFF${csv}\n`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modelo-indices-correcao-construtora.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const applyCorrection = async (parcelaId) => {
    setSaving(true);
    try {
      await api.post(`/construtora/parcelas-receber/${parcelaId}/aplicar-correcao`, {
        data_referencia: new Date().toISOString().slice(0, 10),
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao aplicar correção.');
    } finally {
      setSaving(false);
    }
  };

  const previewCharge = async (parcelaId) => {
    setSaving(true);
    setError('');
    try {
      const response = await api.post(`/construtora/parcelas-receber/${parcelaId}/cobranca-preview`, {});
      setChargePreview(response.data);
      setModal('cobranca');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao gerar prévia de cobrança.');
    } finally {
      setSaving(false);
    }
  };

  const markInstallmentPaid = async (parcelaId) => {
    setSaving(true);
    setError('');
    try {
      await api.post(`/construtora/parcelas-receber/${parcelaId}/marcar-paga`, {
        data_pagamento: new Date().toISOString().slice(0, 10),
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao marcar parcela como paga.');
    } finally {
      setSaving(false);
    }
  };

  const markInstallmentsPaidBulk = async (parcelas, label = 'parcelas filtradas') => {
    const pendentes = (parcelas || []).filter((parcela) => parcela.status !== 'pago');
    if (!pendentes.length) {
      setError('Não há parcelas pendentes nesse filtro.');
      return;
    }
    const total = pendentes.reduce((acc, parcela) => acc + Number(parcela.valor_corrigido || parcela.valor_original || 0), 0);
    const ok = window.confirm(`Marcar ${pendentes.length} ${label} como pagas? Total: ${formatCurrency(total)}.`);
    if (!ok) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/construtora/parcelas-receber/marcar-pagas-em-massa', {
        parcela_ids: pendentes.map((parcela) => parcela.id),
        data_pagamento: new Date().toISOString().slice(0, 10),
        observacoes: `Quitado em massa pelo filtro: ${label}.`,
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao marcar parcelas em massa.');
    } finally {
      setSaving(false);
    }
  };

  const openImplementationAction = (acao) => {
    const map = {
      tabela_vendas: () => setModal('tabela-vendas'),
      novo_cliente: () => setModal('cliente'),
      novo_contrato: () => setModal('contrato'),
      novo_indice: () => setModal('indice'),
      novo_terreno: () => setModal('terreno'),
      novo_pedido: () => setIsOrderModalOpen(true),
      nova_manutencao: () => setModal('manutencao'),
      cliente_dossie: () => setActiveTab('clientes'),
      nova_obra: () => { setActiveTab('obras'); openObraModal(); },
    };
    (map[acao] || (() => {}))();
  };

  const columns = [
    { key: 'name', title: 'Obra', accessor: 'name' },
    { key: 'progress', title: 'Progresso', accessor: 'progress', render: (row) => (
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-accent-primary to-accent-secondary rounded-full"
            style={{ width: `${row.progress}%` }}
          />
        </div>
        <span className="text-sm text-white/60">{row.progress}%</span>
      </div>
    )},
    { key: 'budget', title: 'Orçamento', accessor: 'budget', render: (row) => `R$ ${row.budget.toLocaleString('pt-BR')}` },
    { key: 'deadline', title: 'Prazo', accessor: 'deadline', render: (row) => row.deadline ? formatDate(row.deadline) : '-' },
    { key: 'status', title: 'Status', accessor: 'status', render: (row) => (
      <span className={`px-2 py-1 rounded-full text-xs ${
        row.status === 'concluida' ? 'bg-green-500/20 text-green-400' :
        row.status === 'em_andamento' ? 'bg-blue-500/20 text-blue-400' :
        'bg-yellow-500/20 text-yellow-400'
      }`}>
        {row.status}
      </span>
    )},
    { key: 'acoes', title: 'Ações', render: (row) => (
      <div className="flex flex-wrap gap-2">
        <button className="text-blue-300 hover:text-blue-200" onClick={() => openObraModal(row.raw)}>Editar</button>
        <button className="text-red-300 hover:text-red-200" onClick={() => deleteObra(row.raw)}>Excluir</button>
      </div>
    )},
  ];

  const pedidosColumns = [
    {
      key: 'numero',
      title: 'Pedido/Nota',
      render: (row) => (
        <div>
          <p className="text-white/90">{row.numero || row.id.slice(0, 8)}</p>
          <p className="text-xs text-white/50">{row.fornecedor || 'Fornecedor nao informado'}</p>
        </div>
      ),
    },
    { key: 'obra', title: 'Obra', accessor: 'obra' },
    { key: 'etapa', title: 'Etapa', render: (row) => row.etapa || '-' },
    { key: 'data_vencimento', title: 'Vencimento', render: (row) => formatDate(row.data_vencimento) },
    { key: 'valor_total', title: 'Valor', render: (row) => <span className="text-red-400">{formatCurrency(row.valor_total)}</span> },
    {
      key: 'status',
      title: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.status === 'recebido' ? 'bg-green-500/20 text-green-400' : row.status === 'aprovado' ? 'bg-blue-500/20 text-blue-300' : 'bg-yellow-500/20 text-yellow-400'
        }`}>
          {row.status || 'pendente'}
        </span>
      ),
    },
    {
      key: 'acoes',
      title: 'Acoes',
      render: (row) => row.status === 'recebido' ? (
        <span className="text-xs text-white/40">Recebido</span>
      ) : (
        <div className="flex flex-wrap gap-2">
          {row.status === 'pendente' && <GlassButton size="xs" variant="secondary" icon={CheckCircle2} loading={saving} onClick={() => handleApproveOrder(row.id)}>Aprovar</GlassButton>}
          {row.status === 'aprovado' && <GlassButton size="xs" icon={CheckCircle2} loading={saving} onClick={() => handleReceiveOrder(row.id)}>Receber</GlassButton>}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Construtora</h1>
          <p className="text-white/50">Gestão de obras, cronogramas e custos</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={`${process.env.PUBLIC_URL || ''}/manuals/playbook-humano-financeiro-construtora.md`}
            download
            className="relative inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-accent-primary/50 bg-white/10 text-white/90 border border-white/20 hover:bg-white/20 hover:border-white/30 px-5 py-2.5 text-sm"
          >
            <Download className="w-4 h-4" />
            Manual
          </a>
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>
            Atualizar
          </GlassButton>
          <GlassButton variant="secondary" icon={Upload} onClick={() => openDocumentosOCR('nota_obra')}>
            Enviar nota
          </GlassButton>
          <GlassButton variant="secondary" icon={FileText} onClick={() => openDocumentosOCR('contrato_venda')}>
            Enviar contrato
          </GlassButton>
          <GlassButton variant="secondary" icon={Upload} onClick={() => openDocumentosOCR('cliente_documento')}>
            Doc. cliente
          </GlassButton>
          <GlassButton icon={Plus} onClick={() => setIsOrderModalOpen(true)}>
            Novo pedido
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}

      {success && (
        <GlassCard className="p-4 border-emerald-500/30 bg-emerald-500/10" hover={false}>
          <p className="text-sm text-emerald-300">{success}</p>
        </GlassCard>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Obras Ativas</p>
              <p className="text-2xl font-bold text-white/90">{obras.filter((obra) => obra.status !== 'concluida' && obra.status !== 'cancelada').length}</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-500/20">
              <HardHat className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Orçamento Total</p>
              <p className="text-2xl font-bold text-white/90">{formatCurrency(obras.reduce((acc, obra) => acc + Number(obra.orcamento_previsto || 0), 0))}</p>
            </div>
            <div className="p-3 rounded-xl bg-green-500/20">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Pedidos</p>
              <p className="text-2xl font-bold text-white/90">{pedidos.length}</p>
            </div>
            <div className="p-3 rounded-xl bg-purple-500/20">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
          <p className="text-sm text-white/50">Compras Obra</p>
          <p className="text-2xl font-bold text-white/90">{formatCurrency(totalPedidos)}</p>
            </div>
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <CheckCircle2 className="w-6 h-6 text-accent-primary" />
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">A Receber Clientes</p>
              <p className="text-2xl font-bold text-white/90">{formatCurrency(totalReceber)}</p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-500/20">
              <DollarSign className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 text-white border border-white/10' 
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <FadeIn>
        {activeTab === 'implantacao' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Progresso</p>
                <p className="text-2xl font-semibold text-white/90">{implementationChecklist?.resumo?.progresso_percentual || 0}%</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Pendências</p>
                <p className="text-2xl font-semibold text-white/90">{implementationChecklist?.resumo?.pendentes ?? 0}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Unidades</p>
                <p className="text-2xl font-semibold text-white/90">{implementationChecklist?.resumo?.unidades || 0}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Contratos</p>
                <p className="text-2xl font-semibold text-white/90">{implementationChecklist?.resumo?.contratos || 0}</p>
              </GlassCard>
            </div>
            {(implementationChecklist?.alertas_prazo || []).length > 0 && (
              <GlassCard>
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white/90">Prazos de entrega dos contratos</h3>
                  <p className="text-white/50 text-sm">Alerta pela data prevista e pelo prazo extra contratual de 180 dias</p>
                </div>
                <div className="divide-y divide-white/10">
                  {(implementationChecklist?.alertas_prazo || []).map((alerta) => (
                    <div key={alerta.contrato_id} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 text-sm items-start">
                      <div className="md:col-span-2">
                        <p className="text-white/90 font-semibold">{alerta.titulo}</p>
                        <p className="text-white/50 mt-1">{alerta.descricao}</p>
                      </div>
                      <span className="text-white/60">{alerta.obra || '-'} / Unidade {alerta.unidade || '-'}</span>
                      <span className="text-white/60">{alerta.cliente || '-'}</span>
                      <span className="text-white/60">Entrega: {alerta.data_entrega_prevista ? new Date(alerta.data_entrega_prevista).toLocaleDateString('pt-BR') : '-'}</span>
                      <span className={`w-fit rounded px-2 py-1 text-xs ${alerta.nivel === 'critico' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                        {alerta.nivel === 'critico' ? `Extra vencido há ${alerta.dias_atraso} dias` : `Previsto vencido há ${alerta.dias_atraso} dias`}
                      </span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}
            <GlassCard>
              <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Implantação da Construtora</h3>
                  <p className="text-white/50 text-sm">Checklist para transformar a estrutura em operação com dados reais</p>
                </div>
                <GlassButton variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>Atualizar</GlassButton>
              </div>
              <div className="divide-y divide-white/10">
                {(implementationChecklist?.itens || []).map((item) => (
                  <div key={item.chave} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-4 text-sm items-center">
                    <div className="md:col-span-2">
                      <p className="text-white/90">{item.titulo}</p>
                      <p className="text-white/50">{item.descricao}</p>
                    </div>
                    <span className="text-white/60">{item.total}/{item.minimo}</span>
                    <span className={`w-fit rounded px-2 py-1 text-xs ${item.concluido ? 'bg-green-500/20 text-green-300' : item.prioridade === 'alta' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                      {item.concluido ? 'Concluído' : item.prioridade}
                    </span>
                    <div className="md:col-span-2 flex justify-end">
                      {item.acao && !item.concluido ? (
                        <GlassButton size="xs" onClick={() => openImplementationAction(item.acao)}>Resolver</GlassButton>
                      ) : (
                        <span className="text-white/40">{item.concluido ? 'OK' : '-'}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Próximos passos</h3>
                <p className="text-white/50 text-sm">Ordem prática para começar a alimentar o módulo</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4">
                {(implementationChecklist?.proximos_passos || []).map((item, index) => (
                  <div key={item.chave} className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-white/50 text-xs">Passo {index + 1}</p>
                    <p className="text-white/90 font-semibold mt-1">{item.titulo}</p>
                    <p className="text-white/50 text-sm mt-2">{item.descricao}</p>
                    {item.acao && (
                      <button className="mt-3 text-blue-300 hover:text-blue-200 text-sm" onClick={() => openImplementationAction(item.acao)}>Abrir</button>
                    )}
                  </div>
                ))}
                {!implementationChecklist?.proximos_passos?.length && <p className="text-white/50 text-sm">Checklist completo.</p>}
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'obras' && (
          <GlassCard>
            <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-white/90">Obras reais</h3>
                <p className="text-white/50 text-sm">{obras.length} obra(s) cadastrada(s)</p>
              </div>
              <GlassButton icon={Plus} onClick={() => openObraModal()}>Nova obra</GlassButton>
            </div>
            <GlassTable
              columns={columns}
              data={obras.map((obra) => ({
                ...obra,
                raw: obra,
                name: obra.nome,
                progress: obra.progresso || 0,
                budget: Number(obra.orcamento_previsto || 0),
                deadline: obra.data_fim_prevista,
                status: obra.status,
              }))}
              emptyMessage={loading ? 'Carregando obras...' : 'Nenhuma obra cadastrada. Clique em Nova obra para começar.'}
            />
          </GlassCard>
        )}

        {activeTab === 'comercial' && (
          <div className="space-y-6">
            <GlassCard className="p-5" hover={false}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Operação comercial</h3>
                  <p className="text-white/50 text-sm">Cadastre manualmente ou importe contratos/tabelas para alimentar unidades, clientes e recebíveis.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <GlassButton size="sm" icon={Users} onClick={() => openClientModal()}>Novo cliente</GlassButton>
                  <GlassButton size="sm" variant="secondary" icon={HardHat} onClick={() => setModal('unidade')}>Nova unidade</GlassButton>
                  <GlassButton size="sm" variant="secondary" icon={FileText} onClick={() => setModal('contrato')}>Contrato manual/PDF</GlassButton>
                  <GlassButton size="sm" variant="secondary" icon={Upload} onClick={() => setModal('tabela-vendas')}>Tabela de vendas</GlassButton>
                </div>
              </div>
            </GlassCard>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Unidades</p>
                <p className="text-2xl font-semibold text-white/90">{commercialSummary?.resumo?.unidades_total || unidades.length}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Estoque tabela</p>
                <p className="text-2xl font-semibold text-white/90">{formatCurrency(commercialSummary?.resumo?.valor_estoque_tabela)}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Receber vencido</p>
                <p className="text-2xl font-semibold text-white/90">{formatCurrency(commercialSummary?.resumo?.receber_vencido)}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Próximos 30 dias</p>
                <p className="text-2xl font-semibold text-white/90">{formatCurrency(commercialSummary?.resumo?.receber_30_dias)}</p>
              </GlassCard>
            </div>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Funil das unidades</h3>
                <p className="text-white/50 text-sm">Disponível, reserva, proposta, contrato, venda e entrega</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-7 gap-3 p-4">
                {(commercialSummary?.por_status || []).map((item) => (
                  <div key={item.status} className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
                    <p className="text-white/50 text-xs">{commercialStatusLabels[item.status] || item.status}</p>
                    <p className="text-xl font-semibold text-white/90">{item.quantidade}</p>
                    <p className="text-xs text-white/50">{formatCurrency(item.valor_tabela || item.valor_venda)}</p>
                  </div>
                ))}
              </div>
            </GlassCard>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <GlassCard>
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white/90">Parcelas vencidas</h3>
                  <p className="text-white/50 text-sm">{commercialSummary?.resumo?.parcelas_vencidas || 0} parcelas em atraso</p>
                </div>
                <div className="divide-y divide-white/10">
                  {(commercialSummary?.parcelas_vencidas || []).map((parcela) => (
                    <div key={parcela.id} className="p-4 text-sm space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <span className="text-white/90">{parcela.cliente || '-'}</span>
                        <span className="text-white/60">{parcela.unidade || '-'}</span>
                        <span className="text-white/60">{formatCurrency(parcela.valor_atual)}</span>
                        <span className="text-red-300">{parcela.dias_atraso} dias</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <GlassButton size="xs" variant="secondary" onClick={() => previewCharge(parcela.id)} loading={saving}>Cobrança</GlassButton>
                        <GlassButton size="xs" onClick={() => markInstallmentPaid(parcela.id)} loading={saving}>Pago</GlassButton>
                      </div>
                    </div>
                  ))}
                  {!commercialSummary?.parcelas_vencidas?.length && <p className="p-4 text-white/50 text-sm">Nenhuma parcela vencida.</p>}
                </div>
              </GlassCard>
              <GlassCard>
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white/90">A vencer</h3>
                  <p className="text-white/50 text-sm">{commercialSummary?.resumo?.parcelas_30_dias || 0} parcelas nos próximos 30 dias</p>
                </div>
                <div className="divide-y divide-white/10">
                  {(commercialSummary?.parcelas_30_dias || []).map((parcela) => (
                    <div key={parcela.id} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 text-sm">
                      <span className="text-white/90">{parcela.cliente || '-'}</span>
                      <span className="text-white/60">{parcela.unidade || '-'}</span>
                      <span className="text-white/60">{formatDate(parcela.vencimento)}</span>
                      <span className="text-white/60">{formatCurrency(parcela.valor_atual)}</span>
                      <button className="text-emerald-300 hover:text-emerald-200 text-left" onClick={() => previewCharge(parcela.id)}>cobrança</button>
                    </div>
                  ))}
                  {!commercialSummary?.parcelas_30_dias?.length && <p className="p-4 text-white/50 text-sm">Nenhuma parcela próxima.</p>}
                </div>
              </GlassCard>
            </div>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Unidades em atenção</h3>
                <p className="text-white/50 text-sm">Reservas, propostas, contratos enviados e vendas sem contrato vinculado</p>
              </div>
              <div className="divide-y divide-white/10">
                {(commercialSummary?.unidades_atencao || []).map((unidade) => (
                  <div key={unidade.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-4 text-sm">
                    <span className="text-white/90">{unidade.codigo}</span>
                    <span className="text-white/60">{unidade.obra || '-'}</span>
                    <span className="text-white/60">{unidade.cliente || '-'}</span>
                    <span className="text-white/60">{formatCurrency(unidade.valor_tabela)}</span>
                    <select
                      className="bg-white/10 border border-white/10 rounded px-2 py-1 text-white"
                      value={unidade.status || 'disponivel'}
                      disabled={saving}
                      onChange={(event) => updateUnitStatus(unidade, event.target.value)}
                    >
                      {Object.entries(commercialStatusLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <button className="text-blue-300 hover:text-blue-200 text-left" onClick={() => openUnitDossier(unidade.id)}>Dossiê</button>
                  </div>
                ))}
                {!commercialSummary?.unidades_atencao?.length && <p className="p-4 text-white/50 text-sm">Nenhuma unidade exigindo atenção.</p>}
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'crm' && (
          <div className="space-y-6">
            <GlassCard className="p-5" hover={false}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">CRM da Construtora</h3>
                  <p className="text-white/50 text-sm">Interesses de compra, investidores e clientes para novos empreendimentos. Não substitui o Loft/Vista.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <GlassButton variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>Atualizar</GlassButton>
                  <GlassButton icon={Plus} onClick={() => openLeadModal()}>Nova oportunidade</GlassButton>
                </div>
              </div>
            </GlassCard>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Oportunidades ativas</p>
                <p className="text-2xl font-semibold text-white/90">{leads.filter((lead) => !['ganho', 'perdido'].includes(lead.etapa)).length}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Leads quentes</p>
                <p className="text-2xl font-semibold text-white/90">{leads.filter((lead) => lead.temperatura === 'quente' && !['ganho', 'perdido'].includes(lead.etapa)).length}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Retornos nos próximos 7 dias</p>
                <p className="text-2xl font-semibold text-white/90">{leadAgenda.length}</p>
              </GlassCard>
            </div>
            {leadAgenda.length > 0 && (
              <GlassCard hover={false}>
                <div className="p-4 border-b border-white/10"><h4 className="text-white/90 font-semibold">Agenda comercial</h4></div>
                <div className="divide-y divide-white/10">
                  {leadAgenda.slice(0, 8).map((item, index) => (
                    <div key={item.id || `agenda-${index}`} className="p-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                      <span className="text-white/90">{item.nome || item.descricao || 'Atividade comercial'}</span>
                      <span className="text-white/60">{item.proxima_acao || item.tipo || '-'}</span>
                      <span className="text-white/60">{item.proxima_acao_em ? new Date(item.proxima_acao_em).toLocaleString('pt-BR') : item.data_agendada ? new Date(item.data_agendada).toLocaleString('pt-BR') : '-'}</span>
                      <span className="text-white/50">{item.responsavel || '-'}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}
            <div className="overflow-x-auto pb-3">
              <div className="grid grid-flow-col auto-cols-[280px] gap-4 min-w-max">
                {CRM_STAGES.map(([stage, label]) => {
                  const stageLeads = leads.filter((lead) => lead.etapa === stage);
                  return (
                    <GlassCard key={stage} className="min-h-[390px]" hover={false}>
                      <div className="p-4 border-b border-white/10 flex items-center justify-between gap-2">
                        <div><h4 className="text-white/90 font-semibold">{label}</h4><p className="text-white/45 text-xs">{stageLeads.length} oportunidade(s)</p></div>
                        <button className="text-blue-300 hover:text-blue-200" onClick={() => { setLeadForm({ ...INITIAL_LEAD_FORM, etapa: stage }); setModal('lead'); }} title={`Nova oportunidade em ${label}`}><Plus className="h-4 w-4" /></button>
                      </div>
                      <div className="p-3 space-y-3">
                        {stageLeads.map((lead) => (
                          <div key={lead.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2">
                            <div className="flex items-start justify-between gap-2"><p className="font-medium text-white/90">{lead.nome}</p><span className={`rounded px-2 py-0.5 text-xs ${lead.temperatura === 'quente' ? 'bg-red-500/20 text-red-300' : lead.temperatura === 'frio' ? 'bg-blue-500/20 text-blue-300' : 'bg-yellow-500/20 text-yellow-300'}`}>{lead.temperatura || 'morno'}</span></div>
                            <p className="text-white/50 text-xs">{CRM_ORIGINS.find(([value]) => value === lead.origem_canal)?.[1] || lead.origem_canal}{lead.campanha ? ` · ${lead.campanha}` : ''}</p>
                            <p className="text-white/60 text-xs">{lead.obra || 'Sem obra'}{lead.unidade ? ` · ${lead.unidade}` : ''}</p>
                            {lead.proxima_acao && <p className="text-white/70 text-xs">{lead.proxima_acao}{lead.proxima_acao_em ? ` · ${new Date(lead.proxima_acao_em).toLocaleDateString('pt-BR')}` : ''}</p>}
                            <div className="flex flex-wrap gap-2 pt-1">
                              <button className="text-blue-300 hover:text-blue-200 text-xs" onClick={() => openLeadModal(lead)}>Editar</button>
                              {!lead.cliente_id && <button className="text-emerald-300 hover:text-emerald-200 text-xs" onClick={() => convertLeadToClient(lead)}>Virar cliente</button>}
                            </div>
                            <GlassSelect value={lead.etapa} onChange={(event) => moveLeadStage(lead, event.target.value)} options={CRM_STAGES.map(([value, text]) => ({ value, label: text }))} />
                          </div>
                        ))}
                        {!stageLeads.length && <p className="text-white/40 text-sm">Sem oportunidades.</p>}
                      </div>
                    </GlassCard>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pedidos' && (
          <GlassCard>
            <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-white/90">Pedidos e Notas de Obra</h3>
                <p className="text-white/50 text-sm">{pedidos.length} pedidos registrados</p>
              </div>
              <GlassButton icon={Upload} onClick={() => openDocumentosOCR('nota_obra')}>
                Enviar nota OCR
              </GlassButton>
              <GlassButton variant="secondary" icon={Upload} onClick={() => openDocumentosOCR('pedido_obra')}>
                Enviar pedido
              </GlassButton>
              <GlassButton variant="secondary" icon={Upload} onClick={() => openDocumentosOCR('orcamento_obra')}>
                Enviar orçamento
              </GlassButton>
              <GlassButton variant="secondary" icon={Upload} onClick={() => setIsOrderModalOpen(true)}>
                Importar nota
              </GlassButton>
              <GlassButton variant="secondary" icon={HardHat} onClick={openConsumeModal}>
                Consumir material
              </GlassButton>
            </div>
            <GlassTable
              columns={pedidosColumns}
              data={pedidos}
              searchable={false}
              emptyMessage={loading ? 'Carregando pedidos...' : 'Nenhum pedido de obra registrado'}
            />
          </GlassCard>
        )}

        {activeTab === 'fornecedores' && (
          <div className="space-y-6">
            <GlassCard>
              <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Fornecedores</h3>
                  <p className="text-white/50 text-sm">
                    {fornecedores.length} fornecedor(es) cadastrados para compras de obra.
                  </p>
                </div>
                <GlassButton icon={Plus} onClick={() => openSupplierModal()}>
                  Novo fornecedor
                </GlassButton>
              </div>
              <div className="p-6 border-b border-white/10">
                <GlassInput
                  label="Buscar fornecedor, contato ou material"
                  value={supplierSearch}
                  onChange={(event) => setSupplierSearch(event.target.value)}
                  placeholder="Ex.: bloco, concreto, Piramide..."
                />
              </div>
              <div className="divide-y divide-white/10">
                {filteredFornecedores.map((fornecedor) => (
                  <div key={fornecedor.id} className="grid grid-cols-1 xl:grid-cols-5 gap-4 p-4 text-sm">
                    <div>
                      <p className="font-medium text-white/90">{fornecedor.nome}</p>
                      <p className="text-white/45">{fornecedor.cnpj || 'Sem CNPJ'} · {fornecedor.tipo || 'sem tipo'}</p>
                    </div>
                    <div className="text-white/60">
                      <p>{fornecedor.contato || 'Contato não informado'}</p>
                      <p>{fornecedor.telefone || fornecedor.email || '-'}</p>
                    </div>
                    <div className="text-white/60">
                      <p>{fornecedor.cidade || '-'} {fornecedor.estado ? `/${fornecedor.estado}` : ''}</p>
                      <p>{fornecedor.endereco || ''}</p>
                    </div>
                    <div>
                      <p className="text-white/45">Materiais/serviços</p>
                      <p className="text-white/80">
                        {(fornecedor.materiais || []).slice(0, 3).join(', ') || 'A preencher pelo histórico/notas'}
                      </p>
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-white/60">{fornecedor.pedidos_count || 0} compra(s) · {formatCurrency(fornecedor.total_compras || 0)}</span>
                      <button className="text-blue-300 hover:text-blue-200" onClick={() => openSupplierModal(fornecedor)}>Editar cadastro</button>
                    </div>
                  </div>
                ))}
                {!filteredFornecedores.length && (
                  <p className="p-6 text-sm text-white/50">Nenhum fornecedor encontrado.</p>
                )}
              </div>
            </GlassCard>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <GlassCard className="xl:col-span-2">
                <div className="p-6 border-b border-white/10 flex items-end gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <GlassInput
                      label="Pesquisar histórico por material"
                      value={supplierHistorySearch}
                      onChange={(event) => setSupplierHistorySearch(event.target.value)}
                      placeholder="Ex.: bloco, cimento, concreto, laje..."
                    />
                  </div>
                  <GlassButton variant="secondary" onClick={() => loadSupplierHistory(supplierHistorySearch)} loading={loading}>
                    Pesquisar
                  </GlassButton>
                </div>
                <div className="divide-y divide-white/10">
                  {supplierHistory.map((item) => (
                    <div key={`${item.pedido_id}-${item.descricao}-${item.data}`} className="grid grid-cols-1 lg:grid-cols-6 gap-3 p-4 text-sm">
                      <div className="lg:col-span-2">
                        <p className="font-medium text-white/90">{item.material || 'Compra sem material detalhado'}</p>
                        <p className="text-white/45">{item.numero || 'Sem documento'} · {item.status || '-'}</p>
                      </div>
                      <span className="text-white/70">{item.fornecedor || '-'}</span>
                      <span className="text-white/60">{item.obra || '-'}</span>
                      <span className="text-white/60">{formatDate(item.data)}</span>
                      <div className="text-right lg:text-left">
                        <p className="font-semibold text-white/90">{formatCurrency(item.valor_total || 0)}</p>
                        <p className="text-white/45">
                          {item.quantidade ? `${item.quantidade} ${item.unidade || ''}` : ''}
                          {item.valor_unitario ? ` · ${formatCurrency(item.valor_unitario)}/un` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                  {!supplierHistory.length && (
                    <p className="p-6 text-sm text-white/50">Nenhuma compra encontrada para o termo pesquisado.</p>
                  )}
                </div>
              </GlassCard>

              <GlassCard>
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white/90">Resumo por fornecedor</h3>
                  <p className="text-sm text-white/50">Total encontrado na pesquisa atual.</p>
                </div>
                <div className="p-6 space-y-3">
                  {supplierSummary.map((row) => (
                    <div key={row.fornecedor} className="rounded-xl bg-white/[0.03] p-3">
                      <p className="text-sm font-medium text-white/90">{row.fornecedor}</p>
                      <p className="text-xs text-white/50">{row.compras} registro(s) · {formatCurrency(row.total)}</p>
                    </div>
                  ))}
                  {!supplierSummary.length && <p className="text-sm text-white/50">Sem resumo para exibir.</p>}
                </div>
              </GlassCard>
            </div>
          </div>
        )}

        {activeTab === 'clientes' && (
          <GlassCard>
            <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-white/90">Clientes Compradores</h3>
                <p className="text-white/50 text-sm">{clientes.length} clientes cadastrados na construtora</p>
              </div>
              <GlassButton icon={Plus} onClick={() => openClientModal()}>Novo cliente</GlassButton>
            </div>
            <div className="divide-y divide-white/10">
	              {clientes.map((cliente) => (
	                <div key={cliente.id} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 text-sm">
	                  <span className="text-white/90">{cliente.nome}</span>
	                  <span className="text-white/60">{cliente.cpf_cnpj || '-'}</span>
	                  <span className="text-white/60">{cliente.email || cliente.telefone || '-'}</span>
		                  <span className="text-white/50">{cliente.mailing_lancamentos === false ? 'mailing bloqueado' : cliente.status}</span>
		                  <div className="flex gap-3">
		                    <button className="text-blue-300 hover:text-blue-200 text-left" onClick={() => openClientDossier(cliente.id)}>Dossiê</button>
		                    <button className="text-white/60 hover:text-white text-left" onClick={() => openClientModal(cliente)}>Editar</button>
		                  </div>
		                </div>
		              ))}
              {!clientes.length && <p className="p-6 text-white/50 text-sm">Nenhum cliente comprador cadastrado.</p>}
            </div>
          </GlassCard>
	        )}

        {activeTab === 'parceiros' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Parceiras</p>
                <p className="text-2xl font-semibold text-white/90">{partnerSummary?.parceiras_total || 0}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Ativas</p>
                <p className="text-2xl font-semibold text-white/90">{partnerSummary?.parceiras_ativas || 0}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Autorizações vencidas</p>
                <p className="text-2xl font-semibold text-white/90">{partnerSummary?.autorizacoes_vencidas || 0}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Vendas no ano</p>
                <p className="text-2xl font-semibold text-white/90">{partnerSummary?.vendas_ano_atual || 0}</p>
              </GlassCard>
            </div>
            <GlassCard>
              <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Imobiliárias Parceiras</h3>
                  <p className="text-white/50 text-sm">Controle de autorização, validade e histórico de vendas</p>
                </div>
                <div className="flex gap-2">
                  <GlassButton variant="secondary" icon={Plus} onClick={() => setModal('venda-parceiro')}>Registrar venda</GlassButton>
                  <GlassButton icon={Plus} onClick={() => setModal('parceiro')}>Nova imobiliária</GlassButton>
                </div>
              </div>
              <div className="divide-y divide-white/10">
                {parceiros.map((parceiro) => (
                  <div key={parceiro.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-4 text-sm">
                    <span className="text-white/90 md:col-span-2">{parceiro.nome}</span>
                    <span className="text-white/60">{parceiro.responsavel || '-'}</span>
                    <span className="text-white/60">{parceiro.creci || parceiro.cnpj || '-'}</span>
                    <span className={parceiro.autorizacao_vencida ? 'text-red-300' : 'text-white/60'}>{parceiro.data_fim_autorizacao ? formatDate(parceiro.data_fim_autorizacao) : '-'}</span>
                    <span className="text-white/50">{parceiro.status}</span>
                  </div>
                ))}
                {!parceiros.length && <p className="p-6 text-white/50 text-sm">Nenhuma imobiliária parceira cadastrada.</p>}
              </div>
            </GlassCard>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Vendas por imobiliária</h3>
                <p className="text-white/50 text-sm">Histórico para medir quem realmente vendeu nos últimos anos</p>
              </div>
              <div className="divide-y divide-white/10">
                {vendasParceiros.map((venda) => (
                  <div key={venda.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-4 text-sm">
                    <span className="text-white/90">{formatDate(venda.data_venda)}</span>
                    <span className="text-white/60 md:col-span-2">{venda.imobiliaria}</span>
                    <span className="text-white/60">{venda.unidade || '-'}</span>
                    <span className="text-white/60">{venda.cliente || '-'}</span>
                    <span className="text-white/90">{formatCurrency(venda.valor_venda)}</span>
                  </div>
                ))}
                {!vendasParceiros.length && <p className="p-6 text-white/50 text-sm">Nenhuma venda registrada para parceiras.</p>}
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'unidades' && (
          <GlassCard>
            <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-white/90">Unidades</h3>
                <p className="text-white/50 text-sm">{unidades.length} unidades cadastradas entre obras prontas e em construcao</p>
              </div>
              <div className="flex gap-2">
                <GlassButton variant="secondary" icon={HardHat} onClick={() => setModal('manutencao')}>Manutenção</GlassButton>
                <GlassButton variant="secondary" icon={Upload} onClick={() => setModal('tabela-vendas')}>Tabela vendas</GlassButton>
                <GlassButton icon={Plus} onClick={() => setModal('unidade')}>Nova unidade</GlassButton>
              </div>
            </div>
            <div className="divide-y divide-white/10">
	              {unidades.map((unidade) => (
	                <div key={unidade.id} className="grid grid-cols-1 md:grid-cols-7 gap-3 p-4 text-sm">
	                  <span className="text-white/90">{unidade.codigo}</span>
	                  <span className="text-white/60">{unidade.obra || '-'}</span>
	                  <span className="text-white/60">{unidade.cliente || '-'}</span>
	                  <select
	                    className="bg-white/10 border border-white/10 rounded px-2 py-1 text-white"
	                    value={unidade.status || 'disponivel'}
	                    disabled={saving}
	                    onChange={(event) => updateUnitStatus(unidade, event.target.value)}
	                  >
	                    {Object.entries(commercialStatusLabels).map(([value, label]) => (
	                      <option key={value} value={value}>{label}</option>
	                    ))}
	                  </select>
	                  <span className="text-white/60">{formatCurrency(unidade.valor_tabela)}</span>
	                  <span className="text-white/50">Entrega: {formatDate(unidade.data_entrega_prevista)}</span>
	                  <button className="text-blue-300 hover:text-blue-200 text-left" onClick={() => openUnitDossier(unidade.id)}>Dossiê</button>
                </div>
              ))}
              {!unidades.length && <p className="p-6 text-white/50 text-sm">Nenhuma unidade cadastrada.</p>}
            </div>
          </GlassCard>
        )}

        {activeTab === 'recebiveis' && (
          <div className="space-y-6">
            <GlassCard>
              <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Contratos de Venda</h3>
                  <p className="text-white/50 text-sm">{contratosVenda.length} contratos registrados</p>
                </div>
                <GlassButton icon={Upload} onClick={() => openDocumentosOCR('contrato_venda')}>Enviar contrato/OCR</GlassButton>
              </div>
              <div className="divide-y divide-white/10">
                {contratosVenda.map((contrato) => (
                  <div key={contrato.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-4 text-sm items-center">
                    <span className="text-white/90">{contrato.cliente}</span>
                    <span className="text-white/60">{contrato.unidade}</span>
                    <span className="text-white/60">{formatCurrency(contrato.valor_total)}</span>
                    <span className="text-white/60">{contrato.indice_correcao || '-'}</span>
                    <span className="text-white/50">{contrato.status}</span>
                    <button className="inline-flex items-center gap-1 text-left text-blue-300 hover:text-blue-200" onClick={() => checkContractFinance(contrato)} disabled={saving}>
                      <CheckCircle2 className="h-4 w-4" /> Verificar financeiro
                    </button>
                  </div>
                ))}
                {!contratosVenda.length && <p className="p-6 text-white/50 text-sm">Nenhum contrato de venda registrado.</p>}
              </div>
            </GlassCard>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between gap-4 flex-wrap">
	                  <div>
	                    <h3 className="text-lg font-semibold text-white/90">Parcelas a Receber</h3>
	                    <p className="text-white/50 text-sm">{filteredParcelasReceber.length} de {parcelasReceber.length} parcelas geradas no financeiro</p>
	                  </div>
	                  <GlassButton variant="secondary" icon={Plus} onClick={() => setModal('indice')}>Índice mensal</GlassButton>
	                </div>
		                <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
		                  <GlassSelect label="Mês" value={receivableFilters.mes} onChange={(e) => setReceivableFilters((current) => ({ ...current, mes: e.target.value }))} options={[{ value: '', label: 'Todos' }, ...receivableMonthOptions]} />
		                  <GlassSelect label="Empreendimento" value={receivableFilters.obra} onChange={(e) => setReceivableFilters((current) => ({ ...current, obra: e.target.value }))} options={[{ value: '', label: 'Todos' }, ...[...new Set(parcelasReceber.map((p) => p.obra).filter(Boolean))].sort().map((obra) => ({ value: obra, label: obra }))]} />
		                  <GlassSelect label="Cliente" value={receivableFilters.cliente} onChange={(e) => setReceivableFilters((current) => ({ ...current, cliente: e.target.value }))} options={[{ value: '', label: 'Todos' }, ...[...new Set(parcelasReceber.map((p) => p.cliente).filter(Boolean))].sort().map((cliente) => ({ value: cliente, label: cliente }))]} />
	                  <GlassSelect label="Status" value={receivableFilters.status} onChange={(e) => setReceivableFilters((current) => ({ ...current, status: e.target.value }))} options={[{ value: '', label: 'Todos' }, ...[...new Set(parcelasReceber.map((p) => p.status).filter(Boolean))].sort().map((status) => ({ value: status, label: status }))]} />
	                  <GlassSelect label="Agrupar" value={receivableFilters.grupo} onChange={(e) => setReceivableFilters((current) => ({ ...current, grupo: e.target.value }))} options={[
	                    { value: 'mes', label: 'Por mês' },
	                    { value: 'obra', label: 'Por empreendimento' },
	                    { value: 'cliente', label: 'Por cliente' },
		                    { value: 'status', label: 'Por status' },
		                  ]} />
		                </div>
		                <div className="mt-4 flex justify-end">
		                  <GlassButton
		                    size="sm"
		                    variant="secondary"
		                    disabled={saving || !filteredParcelasReceber.some((parcela) => parcela.status !== 'pago')}
		                    onClick={() => markInstallmentsPaidBulk(filteredParcelasReceber, 'parcelas filtradas')}
		                  >
		                    Marcar filtradas como pagas
		                  </GlassButton>
		                </div>
		              </div>
		              <div className="divide-y divide-white/10">
			                {receivableGroups.map((group) => (
			                  <div key={group.key}>
			                    <div className="px-4 py-3 bg-white/[0.03] flex items-center justify-between gap-3">
			                      <div>
			                        <p className="text-white/90 font-medium">{group.key}</p>
			                        <p className="text-white/45 text-xs">{group.items.length} parcelas</p>
			                      </div>
			                      <div className="flex items-center gap-3">
			                        <span className="text-white/80 font-semibold">{formatCurrency(group.total)}</span>
			                        <button
			                          type="button"
			                          className="text-xs text-emerald-300 hover:text-emerald-200 disabled:text-white/30"
			                          disabled={saving || !group.items.some((parcela) => parcela.status !== 'pago')}
			                          onClick={() => markInstallmentsPaidBulk(group.items, `parcelas de ${group.key}`)}
			                        >
			                          quitar grupo
			                        </button>
			                      </div>
			                    </div>
		                    <div className="divide-y divide-white/10">
		                      {group.items.map((parcela) => (
		                        <div key={parcela.id} className="grid grid-cols-1 md:grid-cols-10 gap-3 p-4 text-sm">
		                          <span className="text-white/90">{parcela.tipo} {parcela.numero}</span>
		                          <span className="text-white/60">{parcela.cliente || '-'}</span>
		                          <span className="text-white/60">{parcela.obra || parcela.unidade || '-'}</span>
		                          <span className="text-white/60">{formatDate(parcela.vencimento)}</span>
		                          <span className="text-white/60">{formatCurrency(parcela.valor_corrigido || parcela.valor_original)}</span>
		                          <span className="text-white/60">{parcela.indice_correcao || '-'}</span>
		                          <span className="text-white/60">{parcela.fase_correcao || '-'}</span>
		                          <span className="text-white/60">Fator {Number(parcela.fator_correcao || 1).toFixed(4)}</span>
		                          <button className="text-emerald-300 hover:text-emerald-200 text-left" disabled={saving} onClick={() => applyCorrection(parcela.id)}>
		                            corrigir
		                          </button>
		                          {parcela.status === 'pago' ? (
		                            <span className="text-white/50">pago</span>
		                          ) : (
		                            <div className="flex gap-2">
		                              <button className="text-blue-300 hover:text-blue-200" onClick={() => previewCharge(parcela.id)}>cobrar</button>
		                              <button className="text-green-300 hover:text-green-200" onClick={() => markInstallmentPaid(parcela.id)}>pago</button>
		                            </div>
		                          )}
		                        </div>
		                      ))}
		                    </div>
		                  </div>
		                ))}
	                {!filteredParcelasReceber.length && <p className="p-6 text-white/50 text-sm">Nenhuma parcela a receber para os filtros selecionados.</p>}
	              </div>
	            </GlassCard>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Índices cadastrados</h3>
                <p className="text-white/50 text-sm">CUB/SC ou INCC durante obra; IGP-M + juros mensal após chaves quando previsto em contrato.</p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                  <GlassInput
                    label="Importar CSV/TXT"
                    type="file"
                    accept=".csv,.txt,text/csv,text/plain"
                    onChange={(event) => setIndexImportFile(event.target.files?.[0] || null)}
                  />
                  <GlassButton variant="secondary" icon={Download} onClick={downloadIndexTemplate}>
                    Modelo CSV
                  </GlassButton>
                  <GlassButton icon={Upload} onClick={importIndices} loading={importing}>
                    Importar índices
                  </GlassButton>
                </div>
                <p className="mt-2 text-xs text-white/40">Colunas aceitas: codigo, competencia, percentual, valor_indice, fonte, observacoes. Use codigos como INCC, CUB_SC, IGPM e IPCA.</p>
              </div>
              <div className="divide-y divide-white/10">
                {indicesCorrecao.slice(0, 12).map((indice) => (
                  <div key={indice.id} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 text-sm">
                    <span className="text-white/90">{indice.codigo}</span>
                    <span className="text-white/60">{indice.competencia}</span>
                    <span className="text-white/60">{Number(indice.percentual || 0).toLocaleString('pt-BR')}%</span>
                    <span className="text-white/50">{indice.fonte || '-'}</span>
                  </div>
                ))}
                {!indicesCorrecao.length && <p className="p-6 text-white/50 text-sm">Nenhum índice cadastrado.</p>}
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'terrenos' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Terrenos</p>
                <p className="text-2xl font-semibold text-white/90">{landSummary?.resumo?.quantidade || terrenos.length}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Valor avaliado</p>
                <p className="text-2xl font-semibold text-white/90">{formatCurrency(landSummary?.resumo?.total_avaliado)}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Banco de terrenos</p>
                <p className="text-2xl font-semibold text-white/90">{formatCurrency(landSummary?.resumo?.banco_terrenos)}</p>
              </GlassCard>
              <GlassCard className="p-5" hover={false}>
                <p className="text-white/50 text-sm">Potencial desenvolvimento</p>
                <p className="text-2xl font-semibold text-white/90">{formatCurrency(landSummary?.resumo?.potencial_desenvolvimento)}</p>
              </GlassCard>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <GlassCard>
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white/90">Inteligência imobiliária</h3>
                  <p className="text-white/50 text-sm">
                    {landIntel?.resumo?.anuncios_analisados || 0} anúncios analisados · {landIntel?.resumo?.anuncios_base_propria || 0} na base própria
                  </p>
                </div>
                <div className="divide-y divide-white/10">
                  {(landIntel?.melhores_regioes || []).slice(0, 5).map((regiao) => (
                    <div key={regiao.regiao} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 text-sm">
                      <span className="text-white/90">{regiao.regiao}</span>
                      <span className="text-white/60">{regiao.quantidade} anúncios</span>
                      <span className="text-white/60">{formatCurrency(regiao.preco_m2_mediano)}/m²</span>
                      <span className="text-white/50">{regiao.tipos_carentes?.length ? regiao.tipos_carentes.join(', ') : regiao.tipo_mais_ofertado || '-'}</span>
                    </div>
                  ))}
                  {!landIntel?.melhores_regioes?.length && <p className="p-4 text-white/50 text-sm">Sem amostra suficiente dos portais para análise regional.</p>}
                </div>
              </GlassCard>
              <GlassCard>
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white/90">Sugestões por terreno</h3>
                  <p className="text-white/50 text-sm">Cruzamento entre localização do terreno e anúncios captados</p>
                </div>
                <div className="divide-y divide-white/10">
                  {(landIntel?.terrenos || []).slice(0, 5).map((item) => (
                    <div key={item.terreno_id} className="p-4 text-sm space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white/90">{item.terreno}</span>
                        <span className="text-white/60">{formatCurrency(item.preco_m2_sugerido)}/m²</span>
                      </div>
                      <p className="text-white/50">Região ref.: {item.regiao_referencia || '-'} · amostra {item.amostra_anuncios || 0}</p>
                      {!!item.sugestoes?.length && <p className="text-white/60">{item.sugestoes.join(' · ')}</p>}
                    </div>
                  ))}
                  {!landIntel?.terrenos?.length && <p className="p-4 text-white/50 text-sm">Cadastre terrenos e alimente os portais para gerar sugestões.</p>}
                </div>
              </GlassCard>
            </div>
            <GlassCard>
              <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Banco de Terrenos e Permutas</h3>
                  <p className="text-white/50 text-sm">{terrenos.length} terrenos em avaliacao, negociacao, banco ou obra</p>
                </div>
                <GlassButton icon={Plus} onClick={() => setModal('terreno')}>Novo terreno</GlassButton>
              </div>
              <div className="divide-y divide-white/10">
                {terrenos.map((terreno) => (
                  <div key={terreno.id} className="grid grid-cols-1 md:grid-cols-8 gap-3 p-4 text-sm">
                    <span className="text-white/90">{terreno.nome}</span>
                    <span className="text-white/60">{terreno.cidade || '-'}</span>
                    <span className="text-white/60">{terreno.area_total || 0} m²</span>
                    <span className="text-white/60">{terreno.proprietario_nome || '-'}</span>
                    <span className="text-white/60">{formatCurrency(terreno.valor_avaliado || terreno.valor_pedido)}</span>
                    <span className="text-white/50">{terreno.status}</span>
                    <span className="text-white/50">{terreno.obra || '-'}</span>
                    <select
                      className="bg-white/10 border border-white/10 rounded px-2 py-1 text-white"
                      value={terreno.status}
                      disabled={saving}
                      onChange={(event) => updateLandStatus(terreno, event.target.value)}
                    >
                      <option value="avaliacao">Avaliação</option>
                      <option value="negociacao">Negociação</option>
                      <option value="banco_terrenos">Banco</option>
                      <option value="obra_desenvolvimento">Desenvolvimento</option>
                      <option value="obra_execucao">Execução</option>
                      <option value="entregue">Entregue</option>
                      <option value="perdido">Perdido</option>
                    </select>
                  </div>
                ))}
                {!terrenos.length && <p className="p-6 text-white/50 text-sm">Nenhum terreno cadastrado.</p>}
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'viabilidade' && (
          <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6">
            <GlassCard className="p-6">
              <div className="mb-5">
                <h3 className="text-lg font-semibold text-white/90">Viabilidade do empreendimento</h3>
                <p className="text-sm text-white/50">Cruza terreno, custo previsto, VGV, permuta, comissão e impostos.</p>
              </div>
              <div className="space-y-4">
                <GlassSelect
                  label="Terreno"
                  value={costForecastForm.terreno_id}
                  onChange={(e) => {
                    const terreno = terrenos.find((item) => item.id === e.target.value);
                    setCostForecastForm({
                      ...costForecastForm,
                      terreno_id: e.target.value,
                      cidade: terreno?.cidade || costForecastForm.cidade,
                      permuta_percentual: terreno?.percentual_permuta || costForecastForm.permuta_percentual,
                      permuta_valor: terreno?.valor_avaliado || terreno?.valor_pedido || costForecastForm.permuta_valor,
                    });
                  }}
                  options={terrenoOptions}
                  placeholder="Sem terreno vinculado"
                />
                <GlassInput
                  label="Área total construída (m²)"
                  type="number"
                  value={costForecastForm.area_total_m2}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, area_total_m2: e.target.value })}
                />
                <GlassInput
                  label="Quantidade de unidades"
                  type="number"
                  value={costForecastForm.unidades}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, unidades: e.target.value })}
                />
                <GlassInput
                  label="Cidade"
                  value={costForecastForm.cidade}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, cidade: e.target.value })}
                />
                <GlassSelect
                  label="Padrão construtivo"
                  value={costForecastForm.padrao}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, padrao: e.target.value })}
                  options={[
                    { value: 'economico', label: 'Econômico' },
                    { value: 'medio', label: 'Médio' },
                    { value: 'alto', label: 'Alto' },
                    { value: 'premium', label: 'Premium' },
                    { value: 'luxo', label: 'Luxo' },
                  ]}
                  placeholder=""
                />
                <GlassSelect
                  label="Método construtivo"
                  value={costForecastForm.tipo_estrutura}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, tipo_estrutura: e.target.value })}
                  options={[
                    { value: 'convencional', label: 'Alvenaria convencional' },
                    { value: 'bloco_estrutural', label: 'Bloco estrutural' },
                    { value: 'misto', label: 'Misto / revisar fases' },
                  ]}
                  placeholder=""
                />
                <GlassInput
                  label="VGV total"
                  type="number"
                  value={costForecastForm.vgv_total}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, vgv_total: e.target.value })}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <GlassInput label="Preço venda/m²" type="number" value={costForecastForm.preco_m2_venda} onChange={(e) => setCostForecastForm({ ...costForecastForm, preco_m2_venda: e.target.value })} />
                  <GlassInput label="Ticket médio/unidade" type="number" value={costForecastForm.ticket_medio_unidade} onChange={(e) => setCostForecastForm({ ...costForecastForm, ticket_medio_unidade: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <GlassInput label="Permuta (%)" type="number" value={costForecastForm.permuta_percentual} onChange={(e) => setCostForecastForm({ ...costForecastForm, permuta_percentual: e.target.value })} />
                  <GlassInput label="Permuta valor" type="number" value={costForecastForm.permuta_valor} onChange={(e) => setCostForecastForm({ ...costForecastForm, permuta_valor: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <GlassInput label="Comissão (%)" type="number" value={costForecastForm.comissao_percentual} onChange={(e) => setCostForecastForm({ ...costForecastForm, comissao_percentual: e.target.value })} />
                  <GlassInput label="Impostos (%)" type="number" value={costForecastForm.impostos_percentual} onChange={(e) => setCostForecastForm({ ...costForecastForm, impostos_percentual: e.target.value })} />
                  <GlassInput label="Marketing (%)" type="number" value={costForecastForm.marketing_percentual} onChange={(e) => setCostForecastForm({ ...costForecastForm, marketing_percentual: e.target.value })} />
                </div>
                <GlassInput label="Outros custos" type="number" value={costForecastForm.outros_custos} onChange={(e) => setCostForecastForm({ ...costForecastForm, outros_custos: e.target.value })} />
                <GlassInput
                  label="Margem de segurança (%)"
                  type="number"
                  value={costForecastForm.margem_seguranca_percentual}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, margem_seguranca_percentual: e.target.value })}
                />
                <GlassSelect
                  label="Obra referência"
                  value={costForecastForm.obra_referencia_id}
                  onChange={(e) => setCostForecastForm({ ...costForecastForm, obra_referencia_id: e.target.value })}
                  options={obraOptions}
                  placeholder="Usar histórico geral"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <GlassSelect
                    label="Corrigir histórico por"
                    value={costForecastForm.indice_atualizacao}
                    onChange={(e) => setCostForecastForm({ ...costForecastForm, indice_atualizacao: e.target.value })}
                    options={[
                      { value: 'INCC', label: 'INCC' },
                      { value: 'CUB_SC', label: 'CUB/SC' },
                      { value: 'IPCA', label: 'IPCA' },
                      { value: 'IGPM', label: 'IGP-M' },
                    ]}
                    placeholder="Sem correção"
                  />
                  <GlassInput
                    label="Competência alvo"
                    type="month"
                    value={costForecastForm.competencia_alvo}
                    onChange={(e) => setCostForecastForm({ ...costForecastForm, competencia_alvo: e.target.value })}
                  />
                </div>
                <GlassButton icon={DollarSign} onClick={handleCostForecast} loading={saving} fullWidth>
                  Calcular viabilidade
                </GlassButton>
              </div>
            </GlassCard>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <GlassCard className="p-5">
                  <p className="text-xs text-white/50">Obras com histórico</p>
                  <p className="mt-2 text-2xl font-bold text-white/90">{costForecastSummary?.indicadores?.obras_com_historico || 0}</p>
                </GlassCard>
                <GlassCard className="p-5">
                  <p className="text-xs text-white/50">Mediana por m²</p>
                  <p className="mt-2 text-xl font-bold text-white/90">{formatCurrency(costForecastSummary?.indicadores?.custo_m2_mediano)}</p>
                </GlassCard>
                <GlassCard className="p-5">
                  <p className="text-xs text-white/50">Faixa baixa m²</p>
                  <p className="mt-2 text-xl font-bold text-white/90">{formatCurrency(costForecastSummary?.indicadores?.custo_m2_baixo)}</p>
                </GlassCard>
                <GlassCard className="p-5">
                  <p className="text-xs text-white/50">Faixa alta m²</p>
                  <p className="mt-2 text-xl font-bold text-white/90">{formatCurrency(costForecastSummary?.indicadores?.custo_m2_alto)}</p>
                </GlassCard>
              </div>

              {costForecast ? (
                <>
                  <GlassCard className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-white/50">Base provável</p>
                        <p className="text-2xl font-bold text-white/90">{formatCurrency(costForecast.custo?.previsao?.custo_base)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Agressivo</p>
                        <p className="text-xl font-semibold text-emerald-300">{formatCurrency(costForecast.custo?.previsao?.custo_agressivo)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Conservador</p>
                        <p className="text-xl font-semibold text-amber-300">{formatCurrency(costForecast.custo?.previsao?.custo_conservador)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Confiança</p>
                        <p className="text-xl font-semibold text-sky-300">{costForecast.custo?.previsao?.confianca || 0}%</p>
                      </div>
                    </div>
	                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/60">
	                      <p>Custo estimado por m²: <span className="text-white/90">{formatCurrency(costForecast.custo?.previsao?.custo_m2_estimado)}</span></p>
	                      <p>Custo estimado por unidade: <span className="text-white/90">{formatCurrency(costForecast.custo?.previsao?.custo_unidade_estimado)}</span></p>
	                    </div>
	                    <div className="mt-4 text-sm text-white/50">
	                      Histórico: {costForecast.custo?.indicadores_historicos?.indice_atualizacao || 'sem correção'} {costForecast.custo?.indicadores_historicos?.competencia_alvo ? `até ${costForecast.custo?.indicadores_historicos?.competencia_alvo}` : ''}
	                    </div>
	                  </GlassCard>

                  <GlassCard className="p-6">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white/90">Resultado da viabilidade</h3>
                        <p className="mt-1 text-sm text-white/50">{costForecast.resultado?.recomendacao}</p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-sm ${costForecast.resultado?.risco === 'baixo' ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : costForecast.resultado?.risco === 'medio' ? 'border-amber-400/30 bg-amber-500/10 text-amber-200' : 'border-red-400/30 bg-red-500/10 text-red-200'}`}>
                        {costForecast.resultado?.decisao || '-'} · risco {costForecast.resultado?.risco || '-'}
                      </span>
                    </div>
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-white/50">VGV</p>
                        <p className="text-xl font-semibold text-white/90">{formatCurrency(costForecast.receita?.vgv_total)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Custo total</p>
                        <p className="text-xl font-semibold text-white/90">{formatCurrency(costForecast.custos?.total)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Lucro estimado</p>
                        <p className={`text-xl font-semibold ${Number(costForecast.resultado?.lucro_estimado || 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{formatCurrency(costForecast.resultado?.lucro_estimado)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Margem</p>
                        <p className="text-xl font-semibold text-sky-300">{Number(costForecast.resultado?.margem_percentual || 0).toLocaleString('pt-BR')}%</p>
                      </div>
                    </div>
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/60">
                      <p>Equilíbrio por m²: <span className="text-white/90">{formatCurrency(costForecast.resultado?.ponto_equilibrio_m2)}</span></p>
                      <p>Equilíbrio por unidade: <span className="text-white/90">{formatCurrency(costForecast.resultado?.ponto_equilibrio_unidade)}</span></p>
                      <p>ROI estimado: <span className="text-white/90">{Number(costForecast.resultado?.roi_percentual || 0).toLocaleString('pt-BR')}%</span></p>
                    </div>
	                    <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs text-white/50">
	                      <span>Obra: {formatCurrency(costForecast.custos?.obra_conservadora)}</span>
	                      <span>Permuta: {formatCurrency(costForecast.custos?.permuta)}</span>
	                      <span>Comissão: {formatCurrency(costForecast.custos?.comissao)}</span>
	                      <span>Impostos: {formatCurrency(costForecast.custos?.impostos)}</span>
	                      <span>Marketing/outros: {formatCurrency(Number(costForecast.custos?.marketing || 0) + Number(costForecast.custos?.outros || 0))}</span>
	                    </div>
	                    <div className="mt-5 flex flex-wrap gap-3">
	                      <GlassButton size="sm" icon={CheckCircle2} onClick={() => saveViabilityStudy('em_analise')} loading={saving}>
	                        Salvar estudo
	                      </GlassButton>
	                      <GlassButton size="sm" variant="success" onClick={() => saveViabilityStudy('aprovado')} loading={saving}>
	                        Salvar aprovado
	                      </GlassButton>
	                      <GlassButton size="sm" variant="outline" onClick={() => saveViabilityStudy('descartado')} loading={saving}>
	                        Salvar descartado
	                      </GlassButton>
	                    </div>
                  </GlassCard>

                  <GlassCard className="p-6">
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white/90">Leitura técnica dos materiais</h3>
                        <p className="mt-1 text-sm text-white/50">Separa notas/pedidos por fase e método para evitar comparar bloco estrutural com alvenaria convencional sem ajuste.</p>
                      </div>
                      <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-white/60">
                        {costForecast.custo?.entrada?.tipo_estrutura || 'convencional'}
                      </span>
                    </div>
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-white/50">Compatível com o método</p>
                        <p className="mt-1 text-xl font-semibold text-emerald-300">{formatCurrency(costForecast.custo?.historico_materiais?.total_compativel)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Exige ajuste técnico</p>
                        <p className="mt-1 text-xl font-semibold text-amber-300">{formatCurrency(costForecast.custo?.historico_materiais?.total_nao_compativel)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/50">Indefinido</p>
                        <p className="mt-1 text-xl font-semibold text-white/80">{formatCurrency(costForecast.custo?.historico_materiais?.total_indefinido)}</p>
                      </div>
                    </div>
                    <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-white/70">Por fase</p>
                        {(costForecast.custo?.historico_materiais?.por_fase || []).slice(0, 6).map((fase) => (
                          <div key={fase.fase}>
                            <div className="flex justify-between text-sm">
                              <span className="text-white/70">{String(fase.fase || '').replaceAll('_', ' ')}</span>
                              <span className="text-white/50">{fase.percentual}% · {formatCurrency(fase.valor_total)}</span>
                            </div>
                            <div className="mt-1 h-2 rounded-full bg-white/10 overflow-hidden">
                              <div className="h-full rounded-full bg-sky-400" style={{ width: `${Math.min(100, Number(fase.percentual || 0))}%` }} />
                            </div>
                          </div>
                        ))}
                        {!costForecast.custo?.historico_materiais?.por_fase?.length && <p className="text-sm text-white/50">Ainda sem material classificado.</p>}
                      </div>
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-white/70">Itens de maior impacto</p>
                        {(costForecast.custo?.historico_materiais?.itens_relevantes || []).slice(0, 5).map((item) => (
                          <div key={`${item.pedido_id}-${item.descricao}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-sm text-white/80">{item.descricao}</p>
                              <span className="shrink-0 text-sm font-semibold text-white/90">{formatCurrency(item.valor_total)}</span>
                            </div>
                            <p className="mt-1 text-xs text-white/45">
                              {item.fornecedor || '-'} · {String(item.fase || '').replaceAll('_', ' ')} · {String(item.metodo || '').replaceAll('_', ' ')}
                              {item.local_obra ? ` · ${item.local_obra}${item.local_confianca ? ` (${item.local_confianca})` : ''}` : ''}
                            </p>
                          </div>
                        ))}
                        {!costForecast.custo?.historico_materiais?.itens_relevantes?.length && <p className="text-sm text-white/50">Sem itens relevantes para exibir.</p>}
                      </div>
                    </div>
                  </GlassCard>

                  <GlassCard className="p-6">
                    <h3 className="text-lg font-semibold text-white/90 mb-4">Curva estimada por etapa</h3>
                    <div className="space-y-3">
                      {(costForecast.custo?.curva_etapas || []).map((etapa) => (
                        <div key={etapa.etapa}>
                          <div className="flex justify-between text-sm">
                            <span className="text-white/80">{etapa.etapa}</span>
                            <span className="text-white/50">{etapa.percentual}% · {formatCurrency(etapa.valor_estimado)}</span>
                          </div>
                          <div className="mt-1 h-2 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full bg-accent-primary" style={{ width: `${Math.min(100, etapa.percentual)}%` }} />
                          </div>
                        </div>
                      ))}
                      {!costForecast.custo?.curva_etapas?.length && <p className="text-sm text-white/50">Sem histórico por etapa suficiente.</p>}
                    </div>
                  </GlassCard>

                  <GlassCard className="p-6">
                    <h3 className="text-lg font-semibold text-white/90 mb-4">Referências usadas</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-left text-white/40">
                          <tr>
                            <th className="py-2">Obra</th>
                            <th className="py-2">Área</th>
                            <th className="py-2">Unid.</th>
                            <th className="py-2">Custo</th>
                            <th className="py-2">R$/m²</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                          {(costForecast.custo?.referencias || []).map((ref) => (
                            <tr key={ref.obra_id} className="text-white/70">
                              <td className="py-2">{ref.nome}</td>
                              <td className="py-2">{Number(ref.area_total_m2 || 0).toLocaleString('pt-BR')} m²</td>
                              <td className="py-2">{ref.unidades}</td>
                              <td className="py-2">{formatCurrency(ref.custo_real)}</td>
                              <td className="py-2">{formatCurrency(ref.custo_m2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GlassCard>

	                  {!!costForecast.alertas?.length && (
	                    <GlassCard className="p-6 border-amber-500/20 bg-amber-500/5">
	                      <h3 className="text-lg font-semibold text-amber-200 mb-3">Alertas da previsão</h3>
	                      <ul className="space-y-2 text-sm text-amber-100/80">
	                        {costForecast.alertas.map((alerta) => <li key={alerta}>• {alerta}</li>)}
	                      </ul>
	                    </GlassCard>
	                  )}
	                </>
	              ) : (
	                <GlassCard className="p-6">
	                  <p className="text-white/60">Preencha os dados da obra nova e calcule para ver custo provável, faixa conservadora e referência histórica.</p>
	                </GlassCard>
	              )}
	              <GlassCard className="p-6">
	                <div className="mb-4 flex items-center justify-between gap-3">
	                  <div>
	                    <h3 className="text-lg font-semibold text-white/90">Estudos salvos</h3>
	                    <p className="text-sm text-white/50">{viabilityStudies.length} cenários registrados</p>
	                  </div>
	                </div>
	                <div className="divide-y divide-white/10">
	                  {viabilityStudies.map((study) => (
	                    <div key={study.id} className="grid grid-cols-1 lg:grid-cols-8 gap-3 py-4 text-sm">
	                      <span className="text-white/90 lg:col-span-2">{study.nome}</span>
	                      <span className="text-white/60">{study.terreno || '-'}</span>
	                      <span className="text-white/60">{formatCurrency(study.vgv_total)}</span>
	                      <span className={Number(study.lucro_estimado || 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}>{formatCurrency(study.lucro_estimado)}</span>
	                      <span className="text-white/60">{Number(study.margem_percentual || 0).toLocaleString('pt-BR')}%</span>
	                      <span className="text-white/50">{study.status}</span>
	                      <div className="flex gap-2">
	                        <button className="text-emerald-300 hover:text-emerald-200" onClick={() => updateViabilityStudy(study, 'aprovado')}>aprovar</button>
	                        <button className="text-red-300 hover:text-red-200" onClick={() => updateViabilityStudy(study, 'descartado')}>descartar</button>
	                        {study.status !== 'virou_obra' && (
	                          <button className="text-sky-300 hover:text-sky-200" onClick={() => convertViabilityStudyToWork(study)}>virar obra</button>
	                        )}
	                      </div>
	                    </div>
	                  ))}
	                  {!viabilityStudies.length && <p className="py-4 text-sm text-white/50">Nenhum estudo salvo ainda.</p>}
	                </div>
	              </GlassCard>
	            </div>
	          </div>
	        )}

        {activeTab === 'cronograma' && (
          <GlassCard className="p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div><h3 className="text-lg font-semibold text-white/90">Cronograma físico-financeiro</h3><p className="mt-1 text-sm text-white/50">Etapas e custos reais vinculados às notas confirmadas.</p></div>
              <div className="flex flex-col gap-3 sm:flex-row"><GlassSelect label="Obra" value={scheduleWorkId} onChange={(e) => { setScheduleWorkId(e.target.value); setScheduleData(null); }} options={obraOptions} placeholder="Selecione a obra" /><GlassButton icon={Plus} onClick={() => { if (!scheduleWorkId) return setError('Selecione a obra antes de cadastrar uma etapa.'); setEditingStageId(''); setEtapaForm(INITIAL_ETAPA_FORM); setModal('etapa'); }}>Nova etapa</GlassButton><GlassButton icon={RefreshCw} onClick={() => loadSchedule()} loading={scheduleLoading}>Carregar</GlassButton></div>
            </div>
            {scheduleData && <div className="mt-6 space-y-3">
	              {(scheduleData.resumo?.etapas_custo_a_frente > 0 || scheduleData.resumo?.etapas_fisico_a_frente > 0) && <div className="rounded-lg border border-amber-400/25 bg-amber-500/10 p-4"><div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between"><div><p className="font-medium text-amber-100">Indicador físico x custo controlado</p><p className="text-xs text-white/60">Compara o avanço físico informado com materiais recebidos e, quando não há custo manual, medições aprovadas de empreiteira. É um sinal para conferência, não uma conclusão automática.</p></div><div className="flex gap-3 text-sm"><span className="text-amber-100">Custo à frente: <b>{scheduleData.resumo?.etapas_custo_a_frente || 0}</b></span><span className="text-sky-100">Físico à frente: <b>{scheduleData.resumo?.etapas_fisico_a_frente || 0}</b></span></div></div></div>}
	              <div className="grid grid-cols-2 lg:grid-cols-8 gap-3 text-sm"><div className="rounded-lg bg-white/[0.03] p-3 text-white/70">Etapas: <b className="text-white">{scheduleData.resumo?.etapas_total || 0}</b></div><div className="rounded-lg bg-white/[0.03] p-3 text-white/70">Planejadas: <b className="text-white">{scheduleData.resumo?.etapas_planejadas || 0}</b></div><div className="rounded-lg bg-amber-500/10 p-3 text-amber-100">Sem etapa: <b>{scheduleData.resumo?.pedidos_sem_etapa || 0}</b></div><div className="rounded-lg bg-white/[0.03] p-3 text-white/70">Previsto: <b className="text-white">{formatCurrency(scheduleData.resumo?.custo_previsto_total)}</b></div><div className="rounded-lg bg-amber-500/10 p-3 text-amber-100">Registrado: <b>{formatCurrency(scheduleData.resumo?.custo_registrado_total)}</b></div><div className="rounded-lg bg-blue-500/10 p-3 text-blue-100">Aprovado: <b>{formatCurrency(scheduleData.resumo?.custo_aprovado_total)}</b></div><div className="rounded-lg bg-emerald-500/10 p-3 text-emerald-100">Recebido: <b>{formatCurrency(scheduleData.resumo?.custo_recebido_total)}</b></div><div className="rounded-lg bg-rose-500/10 p-3 text-rose-100">Atrasadas: <b>{scheduleData.resumo?.etapas_atrasadas || 0}</b></div></div>
	              {(scheduleData.resumo?.medicoes_empreiteira_medidas_total > 0 || scheduleData.resumo?.medicoes_empreiteira_aprovadas_total > 0 || scheduleData.resumo?.medicoes_empreiteira_pagas_total > 0) && <div className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 p-4"><div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between"><div><p className="font-medium text-indigo-100">Mão de obra terceirizada</p><p className="text-xs text-white/50">Medições de empreiteira. O pagamento é refletido do Financeiro; não é estimativa de material.</p></div><p className="text-xs text-white/45">{scheduleData.resumo?.medicoes_empreiteira_sem_etapa || 0} sem etapa formal</p></div><div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3 text-sm"><div className="rounded-md bg-white/[0.04] p-3 text-white/70">Medido <b className="ml-1 text-white">{formatCurrency(scheduleData.resumo?.medicoes_empreiteira_medidas_total)}</b></div><div className="rounded-md bg-white/[0.04] p-3 text-white/70">Aprovado <b className="ml-1 text-white">{formatCurrency(scheduleData.resumo?.medicoes_empreiteira_aprovadas_total)}</b></div><div className="rounded-md bg-white/[0.04] p-3 text-white/70">Pago <b className="ml-1 text-white">{formatCurrency(scheduleData.resumo?.medicoes_empreiteira_pagas_total)}</b></div></div></div>}
	              {scheduleData.classificacao_material?.total_itens_classificados > 0 && <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4"><div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between"><div><p className="font-medium text-white/90">Leitura de custos por método e pavimento</p><p className="text-xs text-white/50">Itens de notas e pedidos. A classificação confirmada por você prevalece; esta leitura não substitui as etapas formais.</p></div><p className="text-sm text-white/80">{formatCurrency(scheduleData.classificacao_material.total_itens_classificados)}</p></div><div className="mt-4 grid gap-4 lg:grid-cols-2"><div><p className="mb-2 text-xs font-medium uppercase tracking-wide text-white/45">Método construtivo</p><div className="space-y-2">{(scheduleData.classificacao_material.por_metodo || []).map((item) => <div key={item.metodo} className="flex items-center justify-between rounded-md bg-white/[0.03] px-3 py-2 text-sm"><span className="text-white/75">{String(item.metodo || '').replaceAll('_', ' ')}</span><span className="text-white/90">{formatCurrency(item.valor_total)} <span className="text-xs text-white/45">({item.percentual}%)</span></span></div>)}</div></div><div><p className="mb-2 text-xs font-medium uppercase tracking-wide text-white/45">Pavimento / local</p><div className="space-y-2">{(scheduleData.classificacao_material.por_pavimento || []).map((item) => <div key={item.local} className="flex items-center justify-between rounded-md bg-white/[0.03] px-3 py-2 text-sm"><span className="text-white/75">{item.local === 'sem_pavimento_identificado' ? 'Sem pavimento identificado' : item.local}</span><span className="text-white/90">{formatCurrency(item.valor_total)} <span className="text-xs text-white/45">({item.confirmados} confirmado(s), {item.inferidos} sugerido(s))</span></span></div>)}</div></div></div></div>}
	              {(scheduleData.etapas || []).map((etapa) => <div key={etapa.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-4"><div className="flex flex-col gap-2 md:flex-row md:justify-between"><div><p className="font-medium text-white/90">{etapa.nome}</p><p className="text-xs text-white/45">{etapa.status} · físico informado: {etapa.progresso || 0}%</p></div><div className="flex items-center gap-3"><p className="text-sm text-white/70">{formatCurrency(etapa.custo_recebido)} recebido · {formatCurrency(etapa.custo_previsto)} previsto</p><GlassButton variant="ghost" onClick={() => { setEditingStageId(etapa.id); setEtapaForm({ ...INITIAL_ETAPA_FORM, ...etapa, custo_previsto: etapa.custo_previsto ?? '', progresso: etapa.progresso ?? 0 }); setModal('etapa'); }}>Editar</GlassButton></div></div><p className="mt-2 text-xs text-white/45">Materiais: registrado {formatCurrency(etapa.custo_registrado)} · aprovado {formatCurrency(etapa.custo_aprovado)} · recebido {formatCurrency(etapa.custo_recebido)} · {etapa.percentual_financeiro == null ? 'cadastre custo previsto e datas para comparar.' : `${etapa.percentual_financeiro}% do custo previsto · ${etapa.pedidos_count} nota(s)/pedido(s)`}</p>{etapa.medicoes_empreiteira_count > 0 && <p className="mt-1 text-xs text-indigo-200/80">Empreiteira: {etapa.medicoes_empreiteira_count} medição(ões) · medido {formatCurrency(etapa.medicoes_empreiteira_medidas)} · aprovado {formatCurrency(etapa.medicoes_empreiteira_aprovadas)} · pago {formatCurrency(etapa.medicoes_empreiteira_pagas)}</p>}</div>)}
              {!scheduleData.etapas?.length && <p className="text-sm text-white/50">Cadastre as etapas da obra para iniciar o cronograma real.</p>}
            </div>}
          </GlassCard>
        )}

        {activeTab === 'custos' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GlassCard className="lg:col-span-2 p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Leitura mensal das fases da obra</h3>
                  <p className="mt-1 text-sm text-white/50">Agrupa notas e pedidos por mês, sugere fase, método e pavimento. Confirme ou ajuste antes de usar no histórico.</p>
                </div>
                <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto">
                  <GlassSelect label="Obra" value={phaseReadingWorkId} onChange={(e) => { setPhaseReadingWorkId(e.target.value); setPhaseReadings([]); }} options={obraOptions} placeholder="Selecione a obra" />
                  <GlassButton icon={RefreshCw} onClick={() => loadPhaseReadings()} loading={phaseReadingLoading}>Analisar notas</GlassButton>
                </div>
              </div>
              {phaseReadings.length > 0 && (
                <div className="mt-5 space-y-3">
                  {phaseReadings.map((reading) => (
                    <div key={reading.competencia} className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <p className="font-medium text-white/90">{reading.competencia === 'sem_data' ? 'Sem data identificada' : reading.competencia}</p>
                          <p className="mt-1 text-sm text-white/60">{String(reading.fase_sugerida || '').replaceAll('_', ' ')} · {String(reading.metodo_sugerido || '').replaceAll('_', ' ')}{reading.local_sugerido ? ` · ${reading.local_sugerido}` : ''}</p>
                          <p className="mt-1 text-xs text-white/45">{reading.pedidos_count} pedido(s) · {formatCurrency(reading.valor_total)} · confiança {reading.confianca}</p>
                        </div>
                        <GlassButton size="sm" variant="secondary" onClick={() => openPhaseConfirmation(reading)}>Conferir e confirmar</GlassButton>
                      </div>
                      <p className="mt-3 text-xs text-white/45">Evidências: {(reading.evidencias || []).map((item) => item.descricao || item.numero).filter(Boolean).join(' · ') || 'sem itens detalhados'}</p>
                    </div>
                  ))}
                </div>
              )}
              {!phaseReadingLoading && phaseReadingWorkId && !phaseReadings.length && <p className="mt-5 text-sm text-white/50">Ainda não há pedidos/notas suficientes para esta obra.</p>}
            </GlassCard>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Distribuição de Custos</h3>
              </div>
              <div className="p-6">
	                <GlassChart
	                  type="pie"
	                  data={costChartData}
	                  dataKeys={['value']}
	                  height={300}
	                />
              </div>
            </GlassCard>
            <GlassCard>
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white/90">Resumo Financeiro</h3>
              </div>
              <div className="p-6 space-y-4">
	                {obras.map((obra) => {
	                  const budget = Number(obra.orcamento_previsto || 0);
	                  const spent = Math.max(Number(obra.orcamento_real || 0), Number(pedidosPorObra[obra.id] || 0));
                  const pct = budget ? Math.min(100, Math.round((spent / budget) * 100)) : Number(obra.progresso || 0);
                  return (
                  <div key={obra.id} className="p-4 rounded-xl bg-white/[0.02]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-white/90">{obra.nome}</span>
                      <span className="text-sm text-white/50">
                        {pct}% {budget ? 'gasto' : 'progresso'}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${pct}%`,
                          backgroundColor: spent > budget && budget ? '#ef4444' : '#6366f1'
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-white/40">
                      <span>{formatCurrency(spent)}</span>
                      <span>{formatCurrency(budget)}</span>
                    </div>
                  </div>
                  );
                })}
                {!obras.length && <p className="text-sm text-white/50">Nenhuma obra cadastrada para resumir custos.</p>}
              </div>
            </GlassCard>
          </div>
        )}
      </FadeIn>

      <GlassModal
        isOpen={modal === 'leitura-fase'}
        onClose={closeModal}
        title="Confirmar leitura de fase"
        icon={CheckCircle2}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={confirmPhaseReading} loading={saving}>Confirmar classificação</GlassButton></>}
      >
        <div className="space-y-4">
          <p className="text-sm text-white/60">A confirmação será aplicada aos {phaseConfirmation?.pedido_ids?.length || 0} pedido(s) desta competência e passa a prevalecer sobre a sugestão automática.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <GlassSelect label="Fase" value={phaseConfirmation?.fase || 'outros'} onChange={(e) => setPhaseConfirmation({ ...phaseConfirmation, fase: e.target.value })} options={[
              { value: 'estrutura_bloco_estrutural', label: 'Estrutura bloco estrutural' }, { value: 'estrutura', label: 'Estrutura / concreto / laje' }, { value: 'alvenaria_reboco', label: 'Alvenaria / reboco' }, { value: 'instalacoes_eletricas', label: 'Instalações elétricas' }, { value: 'instalacoes_hidraulicas', label: 'Instalações hidráulicas' }, { value: 'acabamento_revestimentos', label: 'Revestimentos' }, { value: 'acabamento_pintura', label: 'Pintura' }, { value: 'outros', label: 'Outros / revisar' },
            ]} />
            <GlassSelect label="Método" value={phaseConfirmation?.metodo || 'indefinido'} onChange={(e) => setPhaseConfirmation({ ...phaseConfirmation, metodo: e.target.value })} options={[
              { value: 'bloco_estrutural', label: 'Bloco estrutural' }, { value: 'convencional', label: 'Alvenaria convencional' }, { value: 'compativel', label: 'Compatível aos dois' }, { value: 'indefinido', label: 'Indefinido / revisar' },
            ]} />
            <GlassSelect label="Etapa da obra" value={phaseConfirmation?.etapa_id || ''} onChange={(e) => setPhaseConfirmation({ ...phaseConfirmation, etapa_id: e.target.value })} options={etapas.map((etapa) => ({ value: etapa.id, label: etapa.nome }))} placeholder="Vincular depois" />
            <GlassInput label="Pavimento/local" value={phaseConfirmation?.local_obra || ''} onChange={(e) => setPhaseConfirmation({ ...phaseConfirmation, local_obra: e.target.value })} placeholder="Ex.: 3º pavimento" />
          </div>
          <GlassTextarea label="Observação da confirmação" value={phaseConfirmation?.observacao || ''} onChange={(e) => setPhaseConfirmation({ ...phaseConfirmation, observacao: e.target.value })} />
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'obra'}
        onClose={closeModal}
        title={obraForm.id ? 'Editar obra' : 'Nova obra'}
        icon={HardHat}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveObra} loading={saving}>Salvar obra</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput label="Código" value={obraForm.codigo} onChange={(e) => setObraForm({ ...obraForm, codigo: e.target.value })} />
          <GlassInput label="Nome" value={obraForm.nome} onChange={(e) => setObraForm({ ...obraForm, nome: e.target.value })} />
          <GlassInput label="Cidade" value={obraForm.cidade || ''} onChange={(e) => setObraForm({ ...obraForm, cidade: e.target.value })} />
          <GlassInput label="Estado" value={obraForm.estado || ''} onChange={(e) => setObraForm({ ...obraForm, estado: e.target.value })} />
          <div className="md:col-span-2">
            <GlassInput label="Endereço" value={obraForm.endereco || ''} onChange={(e) => setObraForm({ ...obraForm, endereco: e.target.value })} />
          </div>
          <GlassInput label="Início previsto" type="date" value={obraForm.data_inicio_prevista || ''} onChange={(e) => setObraForm({ ...obraForm, data_inicio_prevista: e.target.value })} />
          <GlassInput label="Fim previsto" type="date" value={obraForm.data_fim_prevista || ''} onChange={(e) => setObraForm({ ...obraForm, data_fim_prevista: e.target.value })} />
          <GlassInput label="Orçamento previsto" type="number" value={obraForm.orcamento_previsto || ''} onChange={(e) => setObraForm({ ...obraForm, orcamento_previsto: e.target.value })} />
          <GlassInput label="Progresso (%)" type="number" value={obraForm.progresso || 0} onChange={(e) => setObraForm({ ...obraForm, progresso: e.target.value })} />
          <GlassSelect label="Status" value={obraForm.status} onChange={(e) => setObraForm({ ...obraForm, status: e.target.value })} options={[
            { value: 'planejamento', label: 'Planejamento' },
            { value: 'em_andamento', label: 'Em andamento' },
            { value: 'pausada', label: 'Pausada' },
            { value: 'concluida', label: 'Concluída' },
            { value: 'cancelada', label: 'Cancelada' },
          ]} />
          <div className="md:col-span-2">
            <GlassTextarea label="Descrição" value={obraForm.descricao || ''} onChange={(e) => setObraForm({ ...obraForm, descricao: e.target.value })} />
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'cobranca'}
        onClose={closeModal}
        title="Prévia de cobrança"
        icon={DollarSign}
        size="lg"
        footer={<GlassButton variant="ghost" onClick={closeModal}>Fechar</GlassButton>}
      >
        {chargePreview ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Cliente</p>
                <p className="text-white/90 font-semibold">{chargePreview.parcela?.cliente || '-'}</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Unidade</p>
                <p className="text-white/90 font-semibold">{chargePreview.parcela?.unidade || '-'}</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Valor</p>
                <p className="text-white/90 font-semibold">{formatCurrency(chargePreview.parcela?.valor_atual)}</p>
              </GlassCard>
            </div>
            <div>
              <p className="text-sm text-white/60 mb-2">E-mail</p>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm text-white/80 whitespace-pre-wrap">
                Para: {chargePreview.email?.to || '-'}{'\n'}
                Assunto: {chargePreview.email?.subject || '-'}{'\n\n'}
                {chargePreview.email?.body || ''}
              </div>
            </div>
            <div>
              <p className="text-sm text-white/60 mb-2">WhatsApp</p>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm text-white/80">
                {chargePreview.whatsapp?.telefone || '-'} · {chargePreview.whatsapp?.mensagem || ''}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-white/50 text-sm">Gerando cobrança...</p>
        )}
      </GlassModal>

      <GlassModal
        isOpen={modal === 'indice'}
        onClose={closeModal}
        title="Índice de correção"
        icon={DollarSign}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveIndex} loading={saving}>Salvar índice</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassSelect
            label="Índice"
            value={indexForm.codigo}
            onChange={(e) => setIndexForm({ ...indexForm, codigo: e.target.value })}
            options={[
              { value: 'CUB_SC', label: 'CUB/SC' },
              { value: 'IGPM', label: 'IGP-M' },
              { value: 'IPCA', label: 'IPCA' },
              { value: 'INCC', label: 'INCC' },
            ]}
          />
          <GlassInput label="Competência" type="month" value={indexForm.competencia} onChange={(e) => setIndexForm({ ...indexForm, competencia: e.target.value })} />
          <GlassInput label="% do mês" type="number" step="0.0001" value={indexForm.percentual} onChange={(e) => setIndexForm({ ...indexForm, percentual: e.target.value })} />
          <GlassInput label="Valor índice (opcional)" type="number" step="0.000001" value={indexForm.valor_indice} onChange={(e) => setIndexForm({ ...indexForm, valor_indice: e.target.value })} />
          <GlassInput label="Fonte" value={indexForm.fonte} onChange={(e) => setIndexForm({ ...indexForm, fonte: e.target.value })} />
          <div className="md:col-span-2">
            <GlassTextarea label="Observações" value={indexForm.observacoes} onChange={(e) => setIndexForm({ ...indexForm, observacoes: e.target.value })} />
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'cliente'}
        onClose={closeModal}
        title="Cliente comprador"
        icon={Users}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveClient} loading={saving}>Salvar cliente</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput label="Nome" value={clientForm.nome} onChange={(e) => setClientForm({ ...clientForm, nome: e.target.value })} />
          <GlassSelect label="Tipo" value={clientForm.tipo_pessoa} onChange={(e) => setClientForm({ ...clientForm, tipo_pessoa: e.target.value })} options={[{ value: 'fisica', label: 'Pessoa física' }, { value: 'juridica', label: 'Pessoa jurídica' }]} />
          <GlassInput label="CPF/CNPJ" value={clientForm.cpf_cnpj} onChange={(e) => setClientForm({ ...clientForm, cpf_cnpj: e.target.value })} />
          <GlassInput label="Telefone" value={clientForm.telefone} onChange={(e) => setClientForm({ ...clientForm, telefone: e.target.value })} />
          <GlassInput label="Email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} />
          <GlassInput label="Cidade" value={clientForm.cidade} onChange={(e) => setClientForm({ ...clientForm, cidade: e.target.value })} />
          <GlassSelect
            label="Mailing lançamentos"
            value={clientForm.mailing_lancamentos === false ? 'nao' : 'sim'}
            onChange={(e) => setClientForm({ ...clientForm, mailing_lancamentos: e.target.value === 'sim' })}
            options={[{ value: 'sim', label: 'Recebe lançamentos' }, { value: 'nao', label: 'Não enviar automático' }]}
          />
          <GlassSelect
            label="Relacionamento"
            value={clientForm.status_relacionamento || 'normal'}
            onChange={(e) => setClientForm({ ...clientForm, status_relacionamento: e.target.value })}
            options={[
              { value: 'normal', label: 'Normal' },
              { value: 'restrito', label: 'Restrito' },
              { value: 'juridico', label: 'Jurídico' },
              { value: 'inadimplente', label: 'Inadimplente' },
            ]}
          />
          <div className="md:col-span-2">
            <GlassInput label="Endereço" value={clientForm.endereco} onChange={(e) => setClientForm({ ...clientForm, endereco: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <GlassTextarea label="Motivo para bloqueio/restrição" value={clientForm.motivo_bloqueio_mailing || ''} onChange={(e) => setClientForm({ ...clientForm, motivo_bloqueio_mailing: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <GlassTextarea label="Observações" value={clientForm.observacoes} onChange={(e) => setClientForm({ ...clientForm, observacoes: e.target.value })} />
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'fornecedor'}
        onClose={closeModal}
        title={supplierForm.id ? 'Editar fornecedor' : 'Novo fornecedor'}
        icon={FileText}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton>{supplierHistoricalLinkMode && supplierForm.id && <GlassButton variant="secondary" onClick={linkExistingSupplierHistory} loading={saving}>Vincular histórico</GlassButton>}<GlassButton onClick={saveSupplier} loading={saving}>Salvar fornecedor</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput label="Nome/Razão social" value={supplierForm.nome} onChange={(e) => setSupplierForm({ ...supplierForm, nome: e.target.value })} />
          <GlassSelect
            label="Tipo"
            value={supplierForm.tipo || 'material'}
            onChange={(e) => setSupplierForm({ ...supplierForm, tipo: e.target.value })}
            options={[
              { value: 'material', label: 'Material' },
              { value: 'servico', label: 'Serviço' },
              { value: 'empreiteira', label: 'Empreiteira' },
              { value: 'equipamento', label: 'Equipamento' },
              { value: 'outro', label: 'Outro' },
            ]}
          />
          <GlassInput label="CNPJ/CPF" value={supplierForm.cnpj || ''} onChange={(e) => setSupplierForm({ ...supplierForm, cnpj: e.target.value })} />
          <GlassInput label="Contato principal" value={supplierForm.contato || ''} onChange={(e) => setSupplierForm({ ...supplierForm, contato: e.target.value })} />
          <GlassInput label="Telefone" value={supplierForm.telefone || ''} onChange={(e) => setSupplierForm({ ...supplierForm, telefone: e.target.value })} />
          <GlassInput label="Email" type="email" value={supplierForm.email || ''} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} />
          <GlassInput label="Cidade" value={supplierForm.cidade || ''} onChange={(e) => setSupplierForm({ ...supplierForm, cidade: e.target.value })} />
          <GlassInput label="Estado" value={supplierForm.estado || ''} onChange={(e) => setSupplierForm({ ...supplierForm, estado: e.target.value })} />
          <GlassSelect
            label="Status"
            value={supplierForm.is_active === false ? 'inativo' : 'ativo'}
            onChange={(e) => setSupplierForm({ ...supplierForm, is_active: e.target.value === 'ativo' })}
            options={[
              { value: 'ativo', label: 'Ativo' },
              { value: 'inativo', label: 'Inativo' },
            ]}
          />
          {!supplierForm.id && (
            <label className="md:col-span-2 flex items-start gap-3 rounded-lg border border-violet-400/20 bg-violet-500/10 px-3 py-3 text-sm text-violet-100">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-violet-500"
                checked={Boolean(supplierForm.vincular_lancamentos_sem_fornecedor)}
                onChange={(e) => setSupplierForm({ ...supplierForm, vincular_lancamentos_sem_fornecedor: e.target.checked })}
              />
              <span>Vincular lançamentos financeiros antigos com este mesmo nome de fornecedor.</span>
            </label>
          )}
          {supplierHistoricalLinkMode && supplierForm.id && (
            <div className="md:col-span-2 rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-3 text-sm text-amber-100">
              Confirme o vínculo apenas se estes lançamentos antigos pertencem a este fornecedor. O ERP relaciona somente itens ativos sem fornecedor e com o mesmo nome cadastrado.
            </div>
          )}
          <div className="md:col-span-2">
            <GlassInput label="Endereço" value={supplierForm.endereco || ''} onChange={(e) => setSupplierForm({ ...supplierForm, endereco: e.target.value })} />
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'parceiro'}
        onClose={closeModal}
        title="Imobiliária parceira"
        icon={Users}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={savePartner} loading={saving}>Salvar parceira</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput label="Nome fantasia" value={partnerForm.nome} onChange={(e) => setPartnerForm({ ...partnerForm, nome: e.target.value })} />
          <GlassInput label="Razão social" value={partnerForm.razao_social} onChange={(e) => setPartnerForm({ ...partnerForm, razao_social: e.target.value })} />
          <GlassInput label="CNPJ" value={partnerForm.cnpj} onChange={(e) => setPartnerForm({ ...partnerForm, cnpj: e.target.value })} />
          <GlassInput label="CRECI" value={partnerForm.creci} onChange={(e) => setPartnerForm({ ...partnerForm, creci: e.target.value })} />
          <GlassInput label="Responsável" value={partnerForm.responsavel} onChange={(e) => setPartnerForm({ ...partnerForm, responsavel: e.target.value })} />
          <GlassInput label="Telefone" value={partnerForm.telefone} onChange={(e) => setPartnerForm({ ...partnerForm, telefone: e.target.value })} />
          <GlassInput label="Email" value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} />
          <GlassInput label="% comissão" type="number" step="0.01" value={partnerForm.percentual_comissao} onChange={(e) => setPartnerForm({ ...partnerForm, percentual_comissao: e.target.value })} />
          <GlassInput label="Início autorização" type="date" value={partnerForm.data_inicio_autorizacao} onChange={(e) => setPartnerForm({ ...partnerForm, data_inicio_autorizacao: e.target.value })} />
          <GlassInput label="Fim autorização" type="date" value={partnerForm.data_fim_autorizacao} onChange={(e) => setPartnerForm({ ...partnerForm, data_fim_autorizacao: e.target.value })} />
          <GlassSelect label="Status" value={partnerForm.status} onChange={(e) => setPartnerForm({ ...partnerForm, status: e.target.value })} options={[{ value: 'ativa', label: 'Ativa' }, { value: 'suspensa', label: 'Suspensa' }, { value: 'encerrada', label: 'Encerrada' }]} />
          <div className="md:col-span-2">
            <GlassTextarea label="Observações" value={partnerForm.observacoes} onChange={(e) => setPartnerForm({ ...partnerForm, observacoes: e.target.value })} />
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'venda-parceiro'}
        onClose={closeModal}
        title="Venda por imobiliária"
        icon={DollarSign}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={savePartnerSale} loading={saving}>Registrar venda</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassSelect label="Imobiliária" value={partnerSaleForm.imobiliaria_id} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, imobiliaria_id: e.target.value })} options={parceiroOptions} />
          <GlassInput label="Data venda" type="date" value={partnerSaleForm.data_venda} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, data_venda: e.target.value })} />
          <GlassSelect label="Unidade" value={partnerSaleForm.unidade_id} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, unidade_id: e.target.value })} options={[{ value: '', label: 'Sem unidade' }, ...unidadeOptions]} />
          <GlassSelect label="Cliente" value={partnerSaleForm.cliente_id} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, cliente_id: e.target.value })} options={[{ value: '', label: 'Sem cliente' }, ...clienteOptions]} />
          <GlassInput label="Corretor" value={partnerSaleForm.corretor_nome} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, corretor_nome: e.target.value })} />
          <GlassInput label="Valor venda" type="number" value={partnerSaleForm.valor_venda} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, valor_venda: e.target.value })} />
          <GlassInput label="Valor comissão" type="number" value={partnerSaleForm.valor_comissao} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, valor_comissao: e.target.value })} />
          <GlassSelect label="Status" value={partnerSaleForm.status} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, status: e.target.value })} options={[{ value: 'registrada', label: 'Registrada' }, { value: 'paga', label: 'Comissão paga' }, { value: 'cancelada', label: 'Cancelada' }]} />
          <div className="md:col-span-2">
            <GlassTextarea label="Observações" value={partnerSaleForm.observacoes} onChange={(e) => setPartnerSaleForm({ ...partnerSaleForm, observacoes: e.target.value })} />
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'unidade'}
        onClose={closeModal}
        title="Unidade"
        icon={HardHat}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveUnit} loading={saving}>Salvar unidade</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassSelect label="Obra" value={unitForm.obra_id} onChange={(e) => setUnitForm({ ...unitForm, obra_id: e.target.value })} options={obraOptions} />
          <GlassInput label="Código" value={unitForm.codigo} onChange={(e) => setUnitForm({ ...unitForm, codigo: e.target.value })} />
          <GlassInput label="Tipo" value={unitForm.tipo} onChange={(e) => setUnitForm({ ...unitForm, tipo: e.target.value })} />
          <GlassInput label="Bloco" value={unitForm.bloco} onChange={(e) => setUnitForm({ ...unitForm, bloco: e.target.value })} />
          <GlassInput label="Pavimento" value={unitForm.pavimento} onChange={(e) => setUnitForm({ ...unitForm, pavimento: e.target.value })} />
          <GlassInput label="Área privativa" type="number" value={unitForm.area_privativa} onChange={(e) => setUnitForm({ ...unitForm, area_privativa: e.target.value })} />
          <GlassInput label="Quartos" type="number" value={unitForm.quartos} onChange={(e) => setUnitForm({ ...unitForm, quartos: e.target.value })} />
          <GlassInput label="Suítes" type="number" value={unitForm.suites} onChange={(e) => setUnitForm({ ...unitForm, suites: e.target.value })} />
          <GlassInput label="Vagas" type="number" value={unitForm.vagas} onChange={(e) => setUnitForm({ ...unitForm, vagas: e.target.value })} />
          <GlassInput label="Valor tabela" type="number" value={unitForm.valor_tabela} onChange={(e) => setUnitForm({ ...unitForm, valor_tabela: e.target.value })} />
          <GlassInput label="Entrega prevista" type="date" value={unitForm.data_entrega_prevista} onChange={(e) => setUnitForm({ ...unitForm, data_entrega_prevista: e.target.value })} />
          <GlassSelect
            label="Status"
            value={unitForm.status}
            onChange={(e) => setUnitForm({ ...unitForm, status: e.target.value })}
            options={Object.entries(commercialStatusLabels).map(([value, label]) => ({ value, label }))}
          />
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'cliente-dossie'}
        onClose={closeModal}
        title="Dossiê do cliente"
        icon={Users}
        size="xl"
        footer={<GlassButton variant="ghost" onClick={closeModal}>Fechar</GlassButton>}
      >
        {clientDossier ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Cliente</p>
                <p className="text-white/90 font-semibold">{clientDossier.cliente?.nome}</p>
                <p className="text-white/50 text-sm">{clientDossier.cliente?.cpf_cnpj || '-'}</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Contato</p>
                <p className="text-white/90 font-semibold">{clientDossier.cliente?.telefone || '-'}</p>
                <p className="text-white/50 text-sm">{clientDossier.cliente?.email || '-'}</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Contratos</p>
                <p className="text-white/90 font-semibold">{clientDossier.contratos?.length || 0}</p>
                <p className="text-white/50 text-sm">{clientDossier.unidades?.length || 0} unidades</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Documentos</p>
                <p className="text-white/90 font-semibold">{clientDossier.documentos?.length || 0}</p>
                <p className="text-white/50 text-sm">{clientDossier.manutencoes?.length || 0} manutenções</p>
              </GlassCard>
            </div>
            <GlassCard hover={false}>
              <div className="p-4 border-b border-white/10">
                <h4 className="text-white/90 font-semibold">Histórico da venda</h4>
              </div>
              <div className="divide-y divide-white/10">
                {(clientDossier.operacoes || []).map((operacao) => (
                  <div key={operacao.id} className="p-4 text-sm grid grid-cols-1 md:grid-cols-5 gap-3">
                    <span className="text-white/90">{operacao.tipo}</span>
                    <span className="text-white/60">{operacao.status}</span>
                    <span className="text-white/60">{operacao.unidade_origem || '-'}</span>
                    <span className="text-white/60">{operacao.unidade_destino || '-'}</span>
                    <span className="text-white/50">{formatDate(operacao.data_operacao)}</span>
                  </div>
                ))}
                {!clientDossier.operacoes?.length && <p className="p-4 text-white/50 text-sm">Nenhuma operação de distrato, aditivo ou troca registrada.</p>}
              </div>
            </GlassCard>
            <GlassCard hover={false}>
              <div className="p-4 border-b border-white/10">
                <h4 className="text-white/90 font-semibold">Anexar documento</h4>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                <GlassInput label="Arquivo" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,application/pdf,image/*" onChange={(e) => setClientDocFile(e.target.files?.[0] || null)} />
                <GlassSelect
                  label="Tipo"
                  value={clientDocForm.tipo}
                  onChange={(e) => setClientDocForm({ ...clientDocForm, tipo: e.target.value })}
                  options={[
                    { value: 'documento', label: 'Documento' },
                    { value: 'contrato', label: 'Contrato' },
                    { value: 'contrato_venda', label: 'Contrato de venda' },
                    { value: 'aditivo_contratual', label: 'Aditivo contratual' },
                    { value: 'distrato', label: 'Distrato' },
                    { value: 'termo_acordo', label: 'Termo de acordo' },
                    { value: 'cessao_direitos', label: 'Cessão de direitos' },
                    { value: 'rg_cpf', label: 'RG/CPF' },
                    { value: 'comprovante', label: 'Comprovante' },
                    { value: 'termo_entrega', label: 'Termo entrega' },
                    { value: 'outro', label: 'Outro' },
                  ]}
                />
                <GlassSelect
                  label="Unidade"
                  value={clientDocForm.unidade_id}
                  onChange={(e) => setClientDocForm({ ...clientDocForm, unidade_id: e.target.value })}
                  options={[{ value: '', label: 'Sem vínculo' }, ...(clientDossier.unidades || []).map((unidade) => ({ value: unidade.id, label: unidade.codigo }))]}
                />
                <GlassInput label="Observação" value={clientDocForm.observacao} onChange={(e) => setClientDocForm({ ...clientDocForm, observacao: e.target.value })} />
                <div className="flex items-end">
                  <GlassButton className="w-full" icon={Upload} onClick={uploadClientDocument} loading={saving}>Anexar</GlassButton>
                </div>
              </div>
            </GlassCard>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <GlassCard hover={false}>
                <div className="p-4 border-b border-white/10">
                  <h4 className="text-white/90 font-semibold">Unidades e contratos</h4>
                </div>
                <div className="divide-y divide-white/10">
                  {(clientDossier.unidades || []).map((unidade) => (
                    <div key={unidade.id} className="p-4 text-sm grid grid-cols-1 md:grid-cols-4 gap-3">
                      <span className="text-white/90">{unidade.codigo}</span>
                      <span className="text-white/60">{unidade.obra || '-'}</span>
                      <span className="text-white/60">{formatCurrency(unidade.valor_venda || unidade.valor_tabela)}</span>
                      <button className="text-blue-300 hover:text-blue-200 text-left" onClick={() => openUnitDossier(unidade.id)}>Unidade</button>
                    </div>
                  ))}
                  {!clientDossier.unidades?.length && <p className="p-4 text-white/50 text-sm">Nenhuma unidade vinculada.</p>}
                </div>
              </GlassCard>
              <GlassCard hover={false}>
                <div className="p-4 border-b border-white/10">
                  <h4 className="text-white/90 font-semibold">Documentos</h4>
                </div>
                <div className="divide-y divide-white/10">
                  {(clientDossier.documentos || []).map((documento) => (
                    <div key={documento.id} className="p-4 text-sm grid grid-cols-1 md:grid-cols-4 gap-3">
                      <span className="text-white/90">{documento.nome_original}</span>
                      <span className="text-white/60">{documento.tipo || 'documento'}</span>
                      <span className="text-white/50">{formatDate(documento.created_at?.slice(0, 10))}</span>
	                      <div className="flex flex-wrap gap-2">
	                        <button className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200 text-left" onClick={() => previewClientDocument(documento)}>
	                          <Eye className="h-4 w-4" /> Abrir
	                        </button>
	                        <button className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200 text-left" onClick={() => downloadClientDocument(documento)}>
	                          <Download className="h-4 w-4" /> Baixar
	                        </button>
	                      </div>
	                    </div>
                  ))}
                  {!clientDossier.documentos?.length && <p className="p-4 text-white/50 text-sm">Nenhum documento anexado.</p>}
                </div>
              </GlassCard>
            </div>
            <GlassCard hover={false}>
              <div className="p-4 border-b border-white/10">
                <h4 className="text-white/90 font-semibold">Parcelas</h4>
              </div>
              <div className="divide-y divide-white/10 max-h-72 overflow-auto">
                {(clientDossier.parcelas || []).map((parcela) => (
                  <div key={parcela.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-3 text-sm">
                    <span className="text-white/90">{parcela.tipo} {parcela.numero}</span>
                    <span className="text-white/60">{formatDate(parcela.vencimento)}</span>
                    <span className="text-white/60">{formatCurrency(parcela.valor_corrigido || parcela.valor_original)}</span>
                    <span className="text-white/60">{parcela.indice_correcao || '-'}</span>
                    <span className="text-white/50">{parcela.status}</span>
                    {parcela.status === 'pago' ? <span className="text-white/40">pago</span> : <button className="text-blue-300 hover:text-blue-200 text-left" onClick={() => previewCharge(parcela.id)}>Cobrança</button>}
                  </div>
                ))}
                {!clientDossier.parcelas?.length && <p className="p-4 text-white/50 text-sm">Nenhuma parcela gerada.</p>}
              </div>
            </GlassCard>
          </div>
        ) : (
          <p className="text-white/50 text-sm">Carregando dossiê...</p>
        )}
      </GlassModal>

      <GlassModal
        isOpen={modal === 'tabela-vendas'}
        onClose={closeModal}
        title="Tabela de vendas"
        icon={Upload}
        size="xl"
        footer={
          <>
            <GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton>
            <GlassButton variant="secondary" onClick={() => importSalesTable(false)} loading={importing}>Gerar prévia</GlassButton>
            <GlassButton onClick={() => importSalesTable(true)} loading={importing} disabled={!salesTablePreview?.items?.length}>Importar unidades</GlassButton>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassSelect label="Obra" value={salesTableForm.obra_id} onChange={(e) => setSalesTableForm({ ...salesTableForm, obra_id: e.target.value })} options={obraOptions} />
            <GlassInput label="Arquivo CSV, TXT ou PDF" type="file" accept=".csv,.txt,.pdf,text/csv,text/plain,application/pdf" onChange={(e) => { setSalesTableFile(e.target.files?.[0] || null); setSalesTablePreview(null); }} />
          </div>
          <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
            <p className="text-sm text-white/60">Campos aceitos: código/unidade, tipo, bloco, pavimento, área, quartos, suítes, vagas, valor, status e entrega prevista.</p>
          </GlassCard>
          {salesTablePreview?.items?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
                <span className="text-white/80">{salesTablePreview.resumo?.identificadas || salesTablePreview.items.length} unidades identificadas</span>
                <span className="text-white/50">{salesTablePreview.resumo?.criadas || 0} novas · {salesTablePreview.resumo?.atualizadas || 0} atualizações</span>
              </div>
              <div className="max-h-80 overflow-auto divide-y divide-white/10 rounded border border-white/10">
                {salesTablePreview.items.slice(0, 80).map((item, index) => (
                  <div key={`${item.codigo}-${index}`} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-3 text-sm">
                    <span className="text-white/90">{item.codigo}</span>
                    <span className="text-white/60">{item.tipo || '-'}</span>
                    <span className="text-white/60">{item.bloco || '-'} {item.pavimento || ''}</span>
                    <span className="text-white/60">{item.area_privativa || '-'} m²</span>
                    <span className="text-white/60">{formatCurrency(item.valor_tabela)}</span>
                    <span className="text-white/50">{item.acao || item.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'unidade-dossie'}
        onClose={closeModal}
        title="Dossiê da unidade"
        icon={FileText}
        size="xl"
        footer={<GlassButton variant="ghost" onClick={closeModal}>Fechar</GlassButton>}
      >
        {unitDossier ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Unidade</p>
                <p className="text-white/90 font-semibold">{unitDossier.unidade?.codigo}</p>
                <p className="text-white/50 text-sm">{unitDossier.unidade?.obra || '-'}</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Cliente</p>
                <p className="text-white/90 font-semibold">{unitDossier.cliente?.nome || 'Sem cliente'}</p>
                <p className="text-white/50 text-sm">{unitDossier.cliente?.cpf_cnpj || '-'}</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">A receber</p>
                <p className="text-white/90 font-semibold">{formatCurrency(unitDossier.resumo?.total_receber)}</p>
                <p className="text-white/50 text-sm">{unitDossier.resumo?.parcelas_pendentes || 0} parcelas pendentes</p>
              </GlassCard>
              <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
                <p className="text-white/50 text-xs">Pós-entrega</p>
                <p className="text-white/90 font-semibold">{unitDossier.resumo?.manutencoes_abertas || 0} abertas</p>
                <p className="text-white/50 text-sm">{formatCurrency(unitDossier.resumo?.total_manutencao)}</p>
              </GlassCard>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <GlassCard hover={false}>
                <div className="p-4 border-b border-white/10">
                  <h4 className="text-white/90 font-semibold">Contratos</h4>
                </div>
                <div className="divide-y divide-white/10">
                  {(unitDossier.contratos || []).map((contrato) => (
                    <div key={contrato.id} className="p-4 text-sm grid grid-cols-1 md:grid-cols-3 gap-3">
                      <span className="text-white/90">{contrato.numero || contrato.id.slice(0, 8)}</span>
                      <span className="text-white/60">{formatCurrency(contrato.valor_total)}</span>
                      <span className="text-white/50">{contrato.indice_correcao || contrato.status}</span>
                    </div>
                  ))}
                  {!unitDossier.contratos?.length && <p className="p-4 text-white/50 text-sm">Nenhum contrato vinculado.</p>}
                </div>
              </GlassCard>
              <GlassCard hover={false}>
                <div className="p-4 border-b border-white/10">
                  <h4 className="text-white/90 font-semibold">Manutenções</h4>
                </div>
                <div className="divide-y divide-white/10">
                  {(unitDossier.manutencoes || []).slice(0, 6).map((item) => (
                    <div key={item.id} className="p-4 text-sm">
                      <p className="text-white/80">{item.problema}</p>
                      <p className="text-white/50">{item.status} · {formatDate(item.data_abertura)} · {formatCurrency(item.custo)}</p>
                    </div>
                  ))}
                  {!unitDossier.manutencoes?.length && <p className="p-4 text-white/50 text-sm">Nenhuma manutenção registrada.</p>}
                </div>
              </GlassCard>
            </div>
            <GlassCard hover={false}>
              <div className="p-4 border-b border-white/10">
                <h4 className="text-white/90 font-semibold">Parcelas</h4>
              </div>
              <div className="divide-y divide-white/10 max-h-80 overflow-auto">
                {(unitDossier.parcelas || []).map((parcela) => (
                  <div key={parcela.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-3 text-sm">
                    <span className="text-white/90">{parcela.tipo} {parcela.numero}</span>
                    <span className="text-white/60">{formatDate(parcela.vencimento)}</span>
                    <span className="text-white/60">{formatCurrency(parcela.valor_corrigido || parcela.valor_original)}</span>
                    <span className="text-white/60">{parcela.indice_correcao || '-'}</span>
                    <span className="text-white/60">{parcela.fase_correcao || '-'}</span>
                    <span className="text-white/50">{parcela.status}</span>
                  </div>
                ))}
                {!unitDossier.parcelas?.length && <p className="p-4 text-white/50 text-sm">Nenhuma parcela gerada.</p>}
              </div>
            </GlassCard>
          </div>
        ) : (
          <p className="text-white/50 text-sm">Carregando dossiê...</p>
        )}
      </GlassModal>

      <GlassModal
        isOpen={modal === 'terreno'}
        onClose={closeModal}
        title="Terreno / permuta"
        icon={HardHat}
        size="xl"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveLand} loading={saving}>Salvar terreno</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput label="Nome" value={landForm.nome} onChange={(e) => setLandForm({ ...landForm, nome: e.target.value })} />
          <GlassInput label="Código" value={landForm.codigo} onChange={(e) => setLandForm({ ...landForm, codigo: e.target.value })} />
          <GlassInput label="Endereço" value={landForm.endereco} onChange={(e) => setLandForm({ ...landForm, endereco: e.target.value })} />
          <GlassInput label="Cidade" value={landForm.cidade} onChange={(e) => setLandForm({ ...landForm, cidade: e.target.value })} />
          <GlassInput label="Área total" type="number" value={landForm.area_total} onChange={(e) => setLandForm({ ...landForm, area_total: e.target.value })} />
          <GlassInput label="Matrícula" value={landForm.matricula} onChange={(e) => setLandForm({ ...landForm, matricula: e.target.value })} />
          <GlassInput label="Proprietário" value={landForm.proprietario_nome} onChange={(e) => setLandForm({ ...landForm, proprietario_nome: e.target.value })} />
          <GlassInput label="Telefone proprietário" value={landForm.proprietario_telefone} onChange={(e) => setLandForm({ ...landForm, proprietario_telefone: e.target.value })} />
          <GlassInput label="Valor pedido" type="number" value={landForm.valor_pedido} onChange={(e) => setLandForm({ ...landForm, valor_pedido: e.target.value })} />
          <GlassInput label="Valor avaliado" type="number" value={landForm.valor_avaliado} onChange={(e) => setLandForm({ ...landForm, valor_avaliado: e.target.value })} />
          <GlassSelect label="Status" value={landForm.status} onChange={(e) => setLandForm({ ...landForm, status: e.target.value })} options={[
            { value: 'avaliacao', label: 'Avaliação' },
            { value: 'negociacao', label: 'Negociação' },
            { value: 'banco_terrenos', label: 'Banco de terrenos' },
            { value: 'obra_desenvolvimento', label: 'Obra em desenvolvimento' },
            { value: 'obra_execucao', label: 'Obra em execução' },
            { value: 'entregue', label: 'Entregue' },
            { value: 'perdido', label: 'Perdido' },
          ]} />
          <GlassSelect label="Obra vinculada" value={landForm.obra_id || ''} onChange={(e) => setLandForm({ ...landForm, obra_id: e.target.value })} options={[{ value: '', label: 'Sem obra vinculada' }, ...obraOptions]} />
          <GlassInput label="% permuta" type="number" value={landForm.percentual_permuta} onChange={(e) => setLandForm({ ...landForm, percentual_permuta: e.target.value })} />
          <GlassInput label="Unidades permuta" type="number" value={landForm.unidades_permuta} onChange={(e) => setLandForm({ ...landForm, unidades_permuta: e.target.value })} />
          <div className="md:col-span-2">
            <GlassTextarea label="Proposta de permuta" value={landForm.proposta_permuta} onChange={(e) => setLandForm({ ...landForm, proposta_permuta: e.target.value })} />
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'reconciliacao-financeira'}
        onClose={closeModal}
        title="Verificar Financeiro do Contrato"
        icon={CheckCircle2}
        size="lg"
        footer={
          <>
            <GlassButton variant="ghost" onClick={closeModal}>Fechar</GlassButton>
            {!reconciliationReport?.applied && (reconciliationReport?.movimentacoes_criadas || reconciliationReport?.movimentacoes_status_atualizadas || reconciliationReport?.movimentacoes_valor_atualizadas) > 0 && (
              <GlassButton onClick={applyContractFinanceReconciliation} loading={saving}>Aplicar ajustes</GlassButton>
            )}
          </>
        }
      >
        {reconciliationReport ? (
          <div className="space-y-5">
            <div>
              <p className="text-white/90 font-semibold">{reconciliationReport.contrato_numero || reconciliationReport.contrato?.numero || 'Contrato sem número'}</p>
              <p className="text-white/50 text-sm">{reconciliationReport.contrato?.cliente || 'Cliente não identificado'} · {reconciliationReport.contrato?.unidade || 'Unidade não identificada'}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <GlassCard className="p-3 bg-white/[0.02]" hover={false}><p className="text-xs text-white/50">Parcelas</p><p className="text-lg font-semibold text-white/90">{reconciliationReport.parcelas || 0}</p></GlassCard>
              <GlassCard className="p-3 bg-white/[0.02]" hover={false}><p className="text-xs text-white/50">Títulos a criar</p><p className="text-lg font-semibold text-white/90">{reconciliationReport.movimentacoes_criadas || 0}</p></GlassCard>
              <GlassCard className="p-3 bg-white/[0.02]" hover={false}><p className="text-xs text-white/50">Status a alinhar</p><p className="text-lg font-semibold text-white/90">{reconciliationReport.movimentacoes_status_atualizadas || 0}</p></GlassCard>
              <GlassCard className="p-3 bg-white/[0.02]" hover={false}><p className="text-xs text-white/50">Valores a alinhar</p><p className="text-lg font-semibold text-white/90">{reconciliationReport.movimentacoes_valor_atualizadas || 0}</p></GlassCard>
            </div>
            {reconciliationReport.resumo_financeiro && (() => {
              const resumo = reconciliationReport.resumo_financeiro;
              return (
                <GlassCard hover={false}>
                  <div className="p-4 border-b border-white/10"><h4 className="text-white/90 font-semibold">Conferência de valores</h4><p className="text-white/50 text-sm">Compare o contrato, o cronograma de recebíveis e os títulos criados no Financeiro.</p></div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="rounded-lg bg-white/[0.03] p-3"><p className="text-white/50">Valor total do contrato</p><p className="text-lg font-semibold text-white/90">{formatCurrency(resumo.valor_contrato)}</p></div>
                    <div className="rounded-lg bg-white/[0.03] p-3"><p className="text-white/50">Soma das parcelas</p><p className="text-lg font-semibold text-white/90">{formatCurrency(resumo.parcelas_total)}</p><p className={Math.abs(Number(resumo.diferenca_contrato_parcelas || 0)) > 0.01 ? 'text-yellow-300 text-xs mt-1' : 'text-emerald-300 text-xs mt-1'}>{Math.abs(Number(resumo.diferenca_contrato_parcelas || 0)) > 0.01 ? `Diferença para contrato: ${formatCurrency(resumo.diferenca_contrato_parcelas)}` : 'Fecha com o valor do contrato'}</p></div>
                    <div className="rounded-lg bg-white/[0.03] p-3"><p className="text-white/50">Soma dos títulos financeiros</p><p className="text-lg font-semibold text-white/90">{formatCurrency(resumo.titulos_total)}</p><p className={Math.abs(Number(resumo.diferenca_parcelas_titulos || 0)) > 0.01 ? 'text-yellow-300 text-xs mt-1' : 'text-emerald-300 text-xs mt-1'}>{Math.abs(Number(resumo.diferenca_parcelas_titulos || 0)) > 0.01 ? `Diferença para parcelas ativas: ${formatCurrency(resumo.diferenca_parcelas_titulos)}` : 'Fecha com as parcelas ativas'}</p></div>
                    <div><p className="text-white/50">Parcelas vinculadas</p><p className="text-white/90">{resumo.parcelas_com_titulo}/{reconciliationReport.parcelas} com título financeiro</p><p className="text-white/50 text-xs">{resumo.parcelas_sem_titulo || 0} sem vínculo</p></div>
                    <div><p className="text-white/50">Pagos</p><p className="text-white/90">Parcelas: {formatCurrency(resumo.parcelas_pagas_total)}</p><p className="text-white/60">Financeiro: {formatCurrency(resumo.titulos_pagos_total)}</p></div>
                    <div><p className="text-white/50">Pendentes</p><p className="text-white/90">Parcelas: {formatCurrency(resumo.parcelas_pendentes_total)}</p><p className="text-white/60">Financeiro: {formatCurrency(resumo.titulos_pendentes_total)}</p></div>
                  </div>
                </GlassCard>
              );
            })()}
            {reconciliationReport.applied ? (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-200 text-sm">Ajustes aplicados e registrados na auditoria. Valores já pagos não foram alterados.</div>
            ) : (reconciliationReport.movimentacoes_criadas || reconciliationReport.movimentacoes_status_atualizadas || reconciliationReport.movimentacoes_valor_atualizadas) > 0 ? (
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 text-yellow-100 text-sm">A prévia encontrou divergências. Revise os avisos abaixo e use “Aplicar ajustes” somente quando concordar.</div>
            ) : (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-200 text-sm">Contrato, recebíveis e Financeiro estão alinhados.</div>
            )}
            {!!reconciliationReport.movimentacoes_canceladas && <p className="text-white/60 text-sm">{reconciliationReport.movimentacoes_canceladas} título(s) futuro(s) serão/foram cancelados por refletirem o status do contrato.</p>}
            {!!reconciliationReport.warnings?.length && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 space-y-2">
                <p className="text-red-200 font-medium text-sm">Atenção antes de usar em caixa</p>
                {reconciliationReport.warnings.map((warning, index) => <p key={`${warning.parcela_id}-${index}`} className="text-red-100 text-sm">{warning.message}</p>)}
              </div>
            )}
            <GlassCard hover={false}>
              <div className="p-4 border-b border-white/10"><h4 className="text-white/90 font-semibold">Alterar regra de correção futura</h4><p className="text-white/50 text-sm">Atualiza somente parcelas pendentes a partir da vigência. Não modifica pagamentos já realizados.</p></div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <GlassSelect label="Índice" value={correctionChangeForm.indice_correcao} onChange={(e) => setCorrectionChangeForm({ ...correctionChangeForm, indice_correcao: e.target.value, juros_mensal_percentual: e.target.value.endsWith('+1') ? '1' : correctionChangeForm.juros_mensal_percentual })} options={[{ value: 'CUB_SC', label: 'CUB/SC' }, { value: 'INCC', label: 'INCC' }, { value: 'SELIC', label: 'SELIC' }, { value: 'SELIC+1', label: 'SELIC + 1% ao mês' }, { value: 'IGPM', label: 'IGP-M' }, { value: 'IGPM+1', label: 'IGP-M + 1% ao mês' }, { value: 'IPCA', label: 'IPCA' }]} />
                <GlassInput label="Juros mensal adicional (%)" type="number" step="0.01" value={correctionChangeForm.juros_mensal_percentual} onChange={(e) => setCorrectionChangeForm({ ...correctionChangeForm, juros_mensal_percentual: e.target.value })} />
                <GlassInput label="Vale a partir de" type="date" value={correctionChangeForm.vigencia_em} onChange={(e) => setCorrectionChangeForm({ ...correctionChangeForm, vigencia_em: e.target.value })} />
                <GlassSelect label="Fase" value={correctionChangeForm.fase_correcao} onChange={(e) => setCorrectionChangeForm({ ...correctionChangeForm, fase_correcao: e.target.value })} options={[{ value: 'obra', label: 'Durante a obra' }, { value: 'pos_chaves', label: 'Após as chaves' }, { value: 'fixa', label: 'Regra fixa' }]} />
                <div className="md:col-span-2"><GlassButton size="sm" variant="secondary" onClick={applyContractCorrectionRule} loading={saving}>Atualizar regra das parcelas pendentes</GlassButton></div>
                {correctionChangeResult && <p className="md:col-span-2 text-emerald-300 text-sm">Regra atualizada em {correctionChangeResult.parcelas_alteradas} parcela(s) pendente(s). Os próximos cálculos usarão {correctionChangeResult.indice_correcao} desde {formatDate(correctionChangeResult.vigencia_em)}.</p>}
              </div>
            </GlassCard>
          </div>
        ) : <p className="text-white/50 text-sm">Carregando verificação...</p>}
      </GlassModal>

      <GlassModal
        isOpen={modal === 'lead'}
        onClose={closeModal}
        title={leadForm.id ? 'Editar oportunidade' : 'Nova oportunidade da Construtora'}
        icon={MessageCircle}
        size="xl"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveLead} loading={saving}>Salvar oportunidade</GlassButton></>}
      >
        <div className="space-y-4">
          <p className="text-sm text-white/50">Registrar o interesse não reserva unidade, não gera contrato e não cria título financeiro.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput label="Nome do interessado" value={leadForm.nome} onChange={(e) => setLeadForm({ ...leadForm, nome: e.target.value })} />
            <GlassInput label="CPF/CNPJ" value={leadForm.cpf_cnpj} onChange={(e) => setLeadForm({ ...leadForm, cpf_cnpj: e.target.value })} />
            <GlassInput label="E-mail" type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} />
            <GlassInput label="Telefone/WhatsApp" value={leadForm.telefone} onChange={(e) => setLeadForm({ ...leadForm, telefone: e.target.value })} />
            <GlassSelect label="De onde veio" value={leadForm.origem_canal} onChange={(e) => setLeadForm({ ...leadForm, origem_canal: e.target.value })} options={CRM_ORIGINS.map(([value, label]) => ({ value, label }))} />
            <GlassInput label="Detalhe da origem" value={leadForm.origem_detalhe} onChange={(e) => setLeadForm({ ...leadForm, origem_detalhe: e.target.value })} placeholder="Ex.: nome do parceiro, feira, formulário" />
            <GlassInput label="Campanha / anúncio" value={leadForm.campanha} onChange={(e) => setLeadForm({ ...leadForm, campanha: e.target.value })} />
            <GlassInput label="Responsável pelo atendimento" value={leadForm.responsavel} onChange={(e) => setLeadForm({ ...leadForm, responsavel: e.target.value })} />
            <GlassSelect label="Empreendimento de interesse" value={leadForm.obra_id} onChange={(e) => setLeadForm({ ...leadForm, obra_id: e.target.value, unidade_id: '' })} options={[{ value: '', label: 'Ainda sem empreendimento definido' }, ...obraOptions]} />
            <GlassSelect label="Unidade de interesse" value={leadForm.unidade_id} onChange={(e) => setLeadForm({ ...leadForm, unidade_id: e.target.value })} options={[{ value: '', label: 'Ainda sem unidade definida' }, ...unidades.filter((unit) => !leadForm.obra_id || unit.obra_id === leadForm.obra_id).map((unit) => ({ value: unit.id, label: `${unit.codigo} · ${unit.obra || ''}` }))]} />
            <GlassInput label="Valor mínimo pretendido" type="number" value={leadForm.valor_minimo} onChange={(e) => setLeadForm({ ...leadForm, valor_minimo: e.target.value })} />
            <GlassInput label="Valor máximo pretendido" type="number" value={leadForm.valor_maximo} onChange={(e) => setLeadForm({ ...leadForm, valor_maximo: e.target.value })} />
            <GlassSelect label="Etapa comercial" value={leadForm.etapa} onChange={(e) => setLeadForm({ ...leadForm, etapa: e.target.value })} options={CRM_STAGES.map(([value, label]) => ({ value, label }))} />
            <GlassSelect label="Temperatura" value={leadForm.temperatura} onChange={(e) => setLeadForm({ ...leadForm, temperatura: e.target.value })} options={[{ value: 'frio', label: 'Frio' }, { value: 'morno', label: 'Morno' }, { value: 'quente', label: 'Quente' }]} />
            <GlassInput label="Próxima ação" value={leadForm.proxima_acao} onChange={(e) => setLeadForm({ ...leadForm, proxima_acao: e.target.value })} placeholder="Ex.: ligar para apresentar tabela" />
            <GlassInput label="Quando retornar" type="datetime-local" value={leadForm.proxima_acao_em} onChange={(e) => setLeadForm({ ...leadForm, proxima_acao_em: e.target.value })} />
            <div className="md:col-span-2"><GlassTextarea label="Observações" value={leadForm.observacoes} onChange={(e) => setLeadForm({ ...leadForm, observacoes: e.target.value })} /></div>
          </div>
          {leadForm.id && (
            <GlassCard hover={false}>
              <div className="p-4 border-b border-white/10"><h4 className="text-white/90 font-semibold">Histórico e próximo contato</h4></div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <GlassSelect label="Tipo" value={leadInteractionForm.tipo} onChange={(e) => setLeadInteractionForm({ ...leadInteractionForm, tipo: e.target.value })} options={[{ value: 'nota', label: 'Nota' }, { value: 'ligacao', label: 'Ligação' }, { value: 'whatsapp', label: 'WhatsApp' }, { value: 'email', label: 'E-mail' }, { value: 'visita', label: 'Visita' }, { value: 'tarefa', label: 'Tarefa' }]} />
                <GlassInput label="Responsável" value={leadInteractionForm.responsavel} onChange={(e) => setLeadInteractionForm({ ...leadInteractionForm, responsavel: e.target.value })} />
                <GlassSelect label="Situação" value={leadInteractionForm.status} onChange={(e) => setLeadInteractionForm({ ...leadInteractionForm, status: e.target.value })} options={[{ value: 'realizada', label: 'Realizada' }, { value: 'agendada', label: 'Agendada' }]} />
                <GlassInput label="Quando (para agendamento)" type="datetime-local" value={leadInteractionForm.data_agendada} onChange={(e) => setLeadInteractionForm({ ...leadInteractionForm, data_agendada: e.target.value })} />
                <div className="md:col-span-2"><GlassTextarea label="Registro" value={leadInteractionForm.descricao} onChange={(e) => setLeadInteractionForm({ ...leadInteractionForm, descricao: e.target.value })} placeholder="Ex.: cliente pediu nova simulação para sexta-feira." /></div>
                <div className="md:col-span-2"><GlassButton size="sm" variant="secondary" onClick={saveLeadInteraction} loading={saving}>Registrar no histórico</GlassButton></div>
              </div>
              <div className="divide-y divide-white/10 max-h-56 overflow-auto">
                {(leadDetail?.interacoes || []).map((item) => (
                  <div key={item.id} className="p-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                    <span className="text-white/90">{item.tipo}</span><span className="text-white/70 md:col-span-2">{item.descricao}</span><span className="text-white/50">{item.data_agendada ? new Date(item.data_agendada).toLocaleString('pt-BR') : item.realizada_em ? new Date(item.realizada_em).toLocaleString('pt-BR') : '-'}</span>
                  </div>
                ))}
                {!leadDetail?.interacoes?.length && <p className="p-3 text-sm text-white/50">Ainda não há histórico registrado.</p>}
              </div>
            </GlassCard>
          )}
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'manutencao'}
        onClose={closeModal}
        title="Manutenção pós-entrega"
        icon={HardHat}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveMaintenance} loading={saving}>Registrar</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassSelect label="Unidade" value={maintenanceForm.unidade_id} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, unidade_id: e.target.value })} options={unidadeOptions} />
          <GlassInput label="Data abertura" type="date" value={maintenanceForm.data_abertura} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, data_abertura: e.target.value })} />
          <div className="md:col-span-2">
            <GlassTextarea label="Problema" value={maintenanceForm.problema} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, problema: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <GlassTextarea label="Solução" value={maintenanceForm.solucao} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, solucao: e.target.value })} />
          </div>
          <GlassInput label="Responsável" value={maintenanceForm.responsavel} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, responsavel: e.target.value })} />
          <GlassInput label="Custo" type="number" value={maintenanceForm.custo} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, custo: e.target.value })} />
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'contrato'}
        onClose={closeModal}
        title="Contrato de venda"
        icon={FileText}
        size="xl"
        footer={<><GlassButton variant="ghost" onClick={closeModal}>Cancelar</GlassButton><GlassButton onClick={saveContract} loading={saving}>Salvar contrato</GlassButton></>}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <GlassSelect label="Cliente" value={contractForm.cliente_id} onChange={(e) => setContractForm({ ...contractForm, cliente_id: e.target.value })} options={clienteOptions} />
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className="text-sm text-blue-300 hover:text-blue-200" onClick={() => openClientModal()}>
                  Cadastrar cliente manualmente
                </button>
                {contractPreview?.extracted?.cliente_nome && !contractForm.cliente_id && (
                  <button type="button" className="text-sm text-emerald-300 hover:text-emerald-200" onClick={createClientFromContractPreview}>
                    Criar cliente da leitura
                  </button>
                )}
              </div>
            </div>
            <div>
              <GlassSelect label="Unidade" value={contractForm.unidade_id} onChange={(e) => setContractForm({ ...contractForm, unidade_id: e.target.value })} options={unidadeOptions} />
              {contractPreview?.extracted?.empreendimento_nome && contractPreview?.extracted?.unidade_codigo && !contractForm.unidade_id && (
                <button type="button" className="mt-2 text-sm text-emerald-300 hover:text-emerald-200" onClick={createWorkUnitFromContractPreview}>
                  Criar obra/unidade da leitura
                </button>
              )}
            </div>
            <GlassInput label="PDF contrato" type="file" accept=".pdf,application/pdf" onChange={(e) => setContractPdfFile(e.target.files?.[0] || null)} />
            <div className="flex items-end">
              <GlassButton icon={Upload} onClick={importContractPdf} loading={importing} className="w-full">Ler contrato</GlassButton>
            </div>
          </div>
          {contractPreview?.extracted && (
            <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
              <div className="space-y-2 text-sm">
                <p className="text-white/80">Leitura: {contractPreview.extracted.cliente_nome || 'cliente não identificado'} · {contractPreview.extracted.empreendimento_nome || 'empreendimento não identificado'} · Unidade {contractPreview.extracted.unidade_codigo || '-'}</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-white/60">
                  <span>Documento: {contractPreview.extracted.cliente_documento || '-'}</span>
                  <span>Valor: {formatCurrency(contractPreview.extracted.valor_total)}</span>
                  <span>Entrada: {formatCurrency(contractPreview.extracted.valor_entrada)}</span>
                  <span>Índice: {contractPreview.extracted.indice_correcao || '-'}</span>
                </div>
	                {!!contractPreview.extracted.parcelas_sugeridas?.length && (
	                  <p className="text-white/50">{contractPreview.extracted.parcelas_sugeridas.length} parcelas sugeridas foram carregadas para revisão.</p>
	                )}
	                <p className="text-white/45">
	                  A leitura preenche os campos, mas o contrato só entra no Comercial depois de selecionar/criar cliente, selecionar unidade e clicar em Salvar contrato.
	                </p>
              </div>
            </GlassCard>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput label="Número" value={contractForm.numero} onChange={(e) => setContractForm({ ...contractForm, numero: e.target.value })} />
            <GlassInput label="Assinatura" type="date" value={contractForm.data_assinatura} onChange={(e) => setContractForm({ ...contractForm, data_assinatura: e.target.value })} />
            <GlassInput label="Entrega prevista" type="date" value={contractForm.data_entrega_prevista} onChange={(e) => setContractForm({ ...contractForm, data_entrega_prevista: e.target.value })} />
            <GlassInput label="Valor total" type="number" value={contractForm.valor_total} onChange={(e) => setContractForm({ ...contractForm, valor_total: e.target.value })} />
            <GlassInput label="Entrada" type="number" value={contractForm.valor_entrada} onChange={(e) => setContractForm({ ...contractForm, valor_entrada: e.target.value })} />
            <GlassInput label="Índice" value={contractForm.indice_correcao} onChange={(e) => setContractForm({ ...contractForm, indice_correcao: e.target.value })} />
            <div className="md:col-span-2">
              <GlassTextarea label="Parcelas: vencimento; valor; tipo; índice; fase; juros mensal %" rows={4} value={contractForm.parcelasTexto || ''} onChange={(e) => setContractForm({ ...contractForm, parcelasTexto: e.target.value })} placeholder={'2026-06-10; 5000; entrada; INCC; obra; 0\n2026-07-10; 2500; parcela; CUB_SC; obra; 0\n2027-02-10; 2500; parcela; IGPM+1; pos_chaves; 1'} />
            </div>
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={modal === 'etapa'}
        onClose={() => setModal('')}
        title="Nova etapa do cronograma"
        icon={Calendar}
        footer={<><GlassButton variant="ghost" onClick={() => setModal('')}>Cancelar</GlassButton><GlassButton onClick={createScheduleStage} loading={saving}>Salvar etapa</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><GlassInput label="Nome da etapa" value={etapaForm.nome} onChange={(e) => setEtapaForm({ ...etapaForm, nome: e.target.value })} placeholder="Ex.: Estrutura - 3º pavimento" /></div>
          <GlassInput label="Ordem" type="number" value={etapaForm.ordem} onChange={(e) => setEtapaForm({ ...etapaForm, ordem: e.target.value })} />
          <GlassSelect label="Status" value={etapaForm.status} onChange={(e) => setEtapaForm({ ...etapaForm, status: e.target.value })} options={[{ value: 'pendente', label: 'Pendente' }, { value: 'em_andamento', label: 'Em andamento' }]} />
          <GlassInput label="Início previsto" type="date" value={etapaForm.data_inicio_prevista} onChange={(e) => setEtapaForm({ ...etapaForm, data_inicio_prevista: e.target.value })} />
          <GlassInput label="Fim previsto" type="date" value={etapaForm.data_fim_prevista} onChange={(e) => setEtapaForm({ ...etapaForm, data_fim_prevista: e.target.value })} />
          <GlassInput label="Custo previsto" type="number" value={etapaForm.custo_previsto} onChange={(e) => setEtapaForm({ ...etapaForm, custo_previsto: e.target.value })} />
          <GlassInput label="Progresso físico (%)" type="number" value={etapaForm.progresso} onChange={(e) => setEtapaForm({ ...etapaForm, progresso: e.target.value })} />
          <div className="md:col-span-2"><GlassTextarea label="Descrição" rows={3} value={etapaForm.descricao} onChange={(e) => setEtapaForm({ ...etapaForm, descricao: e.target.value })} placeholder="Escopo que será acompanhado nesta etapa" /></div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        title="Pedido de Compra da Obra"
        icon={FileText}
        size="xl"
        footer={
          <>
            <GlassButton variant="ghost" onClick={() => setIsOrderModalOpen(false)}>
              Cancelar
            </GlassButton>
            <GlassButton onClick={handleSaveOrder} loading={saving}>
              Salvar pedido
            </GlassButton>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassSelect
              label="Obra"
              value={orderForm.obra_id}
              onChange={(event) => handleObraChange(event.target.value)}
              placeholder="Selecione a obra"
              options={obraOptions}
            />
            <GlassSelect
              label="Etapa"
              value={orderForm.etapa_id}
              onChange={(event) => handleOrderChange('etapa_id', event.target.value)}
              placeholder="Sem etapa"
              options={etapas.map((etapa) => ({ value: etapa.id, label: etapa.nome }))}
            />
            <GlassInput
              label="PDF da nota"
              type="file"
              accept=".pdf,application/pdf"
              onChange={(event) => {
                setPdfFile(event.target.files?.[0] || null);
                setPdfPreview(null);
              }}
            />
            <div className="flex items-end">
              <GlassButton icon={Upload} onClick={handleImportPdf} loading={importing} className="w-full">
                Ler nota
              </GlassButton>
            </div>
          </div>

          {pdfPreview?.extracted && (
            <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-white/50">Leitura da nota</p>
                  <p className="text-xl font-semibold text-white/90">{pdfPreview.extracted.confidence || 0}%</p>
                </div>
                <p className="text-xs text-white/40 text-right max-w-md">
                  Revise os campos e itens antes de salvar o pedido e a conta a pagar.
                </p>
              </div>
            </GlassCard>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput
              label="Fornecedor"
              value={orderForm.fornecedor_nome}
              onChange={(event) => handleOrderChange('fornecedor_nome', event.target.value)}
              placeholder="Fornecedor"
            />
            <GlassInput
              label="Documento"
              value={orderForm.numero}
              onChange={(event) => handleOrderChange('numero', event.target.value)}
              placeholder="NF, pedido, recibo..."
            />
            <GlassInput
              label="Emissao"
              type="date"
              value={orderForm.data_emissao}
              onChange={(event) => handleOrderChange('data_emissao', event.target.value)}
            />
            <GlassInput
              label="Vencimento"
              type="date"
              value={orderForm.data_vencimento}
              onChange={(event) => handleOrderChange('data_vencimento', event.target.value)}
            />
            <GlassInput
              label="Valor total"
              type="number"
              step="0.01"
              value={orderForm.valor_total}
              onChange={(event) => handleOrderChange('valor_total', event.target.value)}
              placeholder="0,00"
            />
            <div className="md:col-span-2">
              <GlassTextarea
                label="Observacoes"
                rows={3}
                value={orderForm.observacoes}
                onChange={(event) => handleOrderChange('observacoes', event.target.value)}
              />
            </div>
          </div>

          {orderForm.itens?.length > 0 && (
            <div className="rounded-lg border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-wide text-white/40 mb-3">Itens lidos da nota</p>
              <div className="space-y-2 max-h-44 overflow-auto">
                {orderForm.itens.map((item, index) => (
                  <div key={`${item.descricao}-${index}`} className="grid grid-cols-[1fr_auto] gap-3 text-sm">
                    <span className="text-white/80">{item.descricao}</span>
                    <span className="text-white/50">
                      {item.quantidade} {item.unidade || ''} · {formatCurrency(item.valor_total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pdfPreview?.extracted?.texto_preview && (
            <div className="rounded-lg border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-wide text-white/40 mb-2">Texto lido</p>
              <p className="text-xs text-white/60 whitespace-pre-wrap max-h-36 overflow-auto">
                {pdfPreview.extracted.texto_preview}
              </p>
            </div>
          )}
        </div>
      </GlassModal>

      <GlassModal
        isOpen={isConsumeModalOpen}
        onClose={() => setIsConsumeModalOpen(false)}
        title="Consumir Material por Etapa"
        icon={HardHat}
        size="lg"
        footer={
          <>
            <GlassButton variant="ghost" onClick={() => setIsConsumeModalOpen(false)}>
              Cancelar
            </GlassButton>
            <GlassButton onClick={handleSaveConsumption} loading={saving}>
              Registrar consumo
            </GlassButton>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassSelect
            label="Obra"
            value={consumeForm.obra_id}
            onChange={(event) => handleConsumeObraChange(event.target.value)}
            placeholder="Selecione a obra"
            options={obraOptions}
          />
          <GlassSelect
            label="Etapa"
            value={consumeForm.etapa_id}
            onChange={(event) => handleConsumeChange('etapa_id', event.target.value)}
            placeholder="Sem etapa"
            options={etapas.map((etapa) => ({ value: etapa.id, label: etapa.nome }))}
          />
          <GlassSelect
            label="Material"
            value={consumeForm.material_id}
            onChange={(event) => handleConsumeMaterialChange(event.target.value)}
            placeholder="Selecione o material"
            options={materiais.map((material) => ({
              value: material.id,
              label: `${material.nome} (${material.estoque_atual || 0} ${material.unidade || 'un'})`,
            }))}
          />
          <GlassInput
            label="Quantidade"
            type="number"
            step="0.001"
            value={consumeForm.quantidade}
            onChange={(event) => handleConsumeChange('quantidade', event.target.value)}
            placeholder="0"
          />
          <GlassInput
            label="Valor unitario"
            type="number"
            step="0.01"
            value={consumeForm.valor_unitario}
            onChange={(event) => handleConsumeChange('valor_unitario', event.target.value)}
            placeholder="0,00"
          />
          <div className="md:col-span-2">
            <GlassTextarea
              label="Observacoes"
              rows={3}
              value={consumeForm.observacoes}
              onChange={(event) => handleConsumeChange('observacoes', event.target.value)}
              placeholder="Ex.: aplicado na fundacao, ajuste de estoque..."
            />
          </div>
        </div>
      </GlassModal>
    </div>
  );
};

export default Construtora;
